import React, { useState } from "react";
import {
  ShieldAlert, Boxes, TimerReset, Activity, LayoutDashboard, Lightbulb,
  Factory, FlaskConical, Beaker, Car, CalendarClock, TerminalSquare,
  FileSpreadsheet, Mail, ChevronRight, ArrowRight, ArrowUpRight, Layers,
  ExternalLink, HelpCircle, Server, Database, Cpu, Wrench, FileText,
  Building2, Share2, Bot, CheckCircle2, Bell, Lock, ShieldCheck,
  TrendingDown, BadgeCheck, MapPin, Linkedin, Zap, Utensils,
  Megaphone, Compass, Handshake, Target, BookOpen, Coins, Gauge, Users,
  TrendingUp, LineChart,
} from "lucide-react";
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  Line, ComposedChart, Legend, LabelList, ReferenceLine,
} from "recharts";
import { motion, useReducedMotion } from "framer-motion";
import { SectionShell } from "./components/SectionShell";
import { ReviewWarning } from "./components/ReviewWarning";
import ArchitectureDiagram from "./components/ArchitectureDiagram";
import { useMode } from "./context/ModeContext";
import { trackCtaClick } from "./lib/analytics";
import { api } from "./lib/api";
import { toast } from "sonner";

const ICONS = {
  ShieldAlert, Boxes, TimerReset, Activity, LayoutDashboard, Lightbulb,
  Factory, FlaskConical, Beaker, Car, CalendarClock, TerminalSquare,
  FileSpreadsheet, Mail, Layers, Server, Database, Cpu, Wrench, FileText,
  Building2, Share2, Bot, CheckCircle2, Bell, Lock, TrendingDown, BadgeCheck,
  Megaphone, Compass, Handshake, Target, BookOpen, Coins, Gauge, Users,
  ShieldCheck, MapPin, Zap, Utensils, TrendingUp, LineChart,
};
const Icon = ({ name, ...p }) => {
  const C = ICONS[name] || HelpCircle;
  return <C {...p} />;
};

function PilotLogo({ pilot }) {
  const [err, setErr] = useState(false);
  if (pilot.domain && !err) {
    return (
      <div className="flex h-11 w-11 items-center justify-center border border-line2 bg-white p-1.5">
        <img
          src={`https://www.google.com/s2/favicons?domain=${pilot.domain}&sz=128`}
          onError={() => setErr(true)}
          alt={pilot.name}
          className="max-h-full max-w-full object-contain"
        />
      </div>
    );
  }
  return (
    <div className="flex h-11 w-11 items-center justify-center border border-line2 bg-ink">
      <Icon name={pilot.icon} className="w-5 h-5 text-sky" />
    </div>
  );
}

function PartnerLogo({ partner }) {
  const [err, setErr] = useState(false);
  const src = partner.logo
    ? partner.logo
    : partner.domain
      ? `https://www.google.com/s2/favicons?domain=${partner.domain}&sz=128`
      : null;
  const showImg = src && !err;
  return (
    <div data-testid={`partner-${partner.name.toLowerCase()}`} className="flex items-center gap-2.5 border border-line2 bg-panel px-3.5 py-2.5">
      <div className="flex h-7 w-7 items-center justify-center bg-white rounded-sm p-0.5 shrink-0">
        {showImg ? (
          <img src={src} onError={() => setErr(true)} alt={partner.name} className="max-h-full max-w-full object-contain" />
        ) : (
          <span className="font-heading font-black text-xs text-ink">{partner.name.slice(0, 2).toUpperCase()}</span>
        )}
      </div>
      <span className="font-mono-data text-sm font-semibold text-ftext">{partner.name}</span>
    </div>
  );
}

function PartnerLogoBig({ partner }) {
  const clearbit = partner.domain ? `https://logo.clearbit.com/${partner.domain}` : null;
  const favicon = partner.domain ? `https://www.google.com/s2/favicons?domain=${partner.domain}&sz=256` : null;
  const initial = partner.logo || clearbit || favicon;
  const [src, setSrc] = useState(initial);
  const [failed, setFailed] = useState(false);
  const onError = () => {
    if (partner.logo && src === partner.logo && clearbit) { setSrc(clearbit); return; }
    if (src === clearbit && favicon) { setSrc(favicon); return; }
    setFailed(true);
  };
  return (
    <div data-testid={`partner-${partner.name.toLowerCase()}`} className="flex flex-col items-center gap-1.5 border border-line2 bg-ink px-4 py-3 w-[150px] transition-colors hover:border-sky">
      <div className="flex h-14 w-full items-center justify-center bg-white rounded-sm p-2">
        {src && !failed ? (
          <img src={src} onError={onError} alt={partner.name} className="max-h-full max-w-full object-contain" />
        ) : (
          <span className="font-heading font-black text-xl text-ink">{partner.name.slice(0, 2).toUpperCase()}</span>
        )}
      </div>
      <span className="font-mono-data text-[11px] font-bold uppercase tracking-wider text-ftext text-center">{partner.name}</span>
    </div>
  );
}

const chartTooltip = {
  contentStyle: {
    background: "#0D1220",
    border: "1px solid #1E293B",
    borderRadius: 0,
    fontFamily: "JetBrains Mono, monospace",
    fontSize: 12,
    color: "#F1F5F9",
  },
  labelStyle: { color: "#94A3B8" },
};

/* ------------------------------------------------------------------ Welcome */
export function Welcome({ data, common }) {
  const w = data.welcome;
  const jump = (id, key) => {
    trackCtaClick(key, { from: "hero" });
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <SectionShell id="welcome" {...common}>
      <div>
        <div className="grid gap-12 lg:grid-cols-1 lg:items-center">
          {/* Left: headline + subhead + CTAs */}
          <div>
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <div className="inline-flex items-center gap-2 font-mono-data font-bold text-sm md:text-base uppercase tracking-[0.2em] text-sky border border-sky/50 px-4 py-1.5">
                {w.eyebrow}
              </div>
            </div>
            <h1 className="font-heading font-black tracking-tight text-3xl md:text-4xl lg:text-[2.75rem] leading-[1.05]">
              {w.headline_lines.map((line, i) => (
                <span
                  key={i}
                  className={`block whitespace-nowrap ${i === w.headline_lines.length - 1 ? "text-sky" : "text-ftext"}`}
                >
                  {line}
                </span>
              ))}
            </h1>
            {w.tagline && (
              <p data-testid="hero-tagline" className="mt-3 text-xl md:text-2xl text-ftext font-semibold leading-snug">{w.tagline}</p>
            )}
            {w.hook && (
              <p data-testid="hero-hook" className="mt-5 text-2xl md:text-3xl text-ftext font-heading font-black leading-tight max-w-3xl">{w.hook}</p>
            )}
            {w.proof_chips && (
              <div data-testid="hero-proof-chips" className="mt-6 flex flex-wrap gap-2.5">
                {w.proof_chips.map((c, i) => (
                  <div key={i} className="inline-flex items-center gap-2 border border-sky/50 bg-sky/5 px-3.5 py-1.5 font-mono-data text-[11px] md:text-xs font-bold uppercase tracking-wider text-ftext">
                    <CheckCircle2 className="w-3.5 h-3.5 text-sky" />
                    {c.label}
                  </div>
                ))}
              </div>
            )}
            {w.why_now && (
              <p data-testid="hero-why-now" className="mt-5 text-sm text-ftext3 italic max-w-3xl leading-snug">
                <span className="font-mono-data not-italic font-black uppercase tracking-wider text-[#10B981] mr-2">Why now</span>
                {w.why_now}
              </p>
            )}
            {w.core_messages?.problem && (
              <div data-testid="hero-core-problem" className="mt-6 border-l-4 border-sky bg-panel/60 px-4 py-3 max-w-3xl">
                <div className="font-mono-data text-[10px] font-black uppercase tracking-[0.18em] text-sky mb-1">The problem we solve</div>
                <p className="text-sm md:text-base text-ftext leading-snug">{w.core_messages.problem}</p>
              </div>
            )}
            {w.core_messages?.value && (
              <div data-testid="hero-core-value" className="mt-3 border-l-4 border-[#10B981] bg-panel/60 px-4 py-3 max-w-3xl">
                <div className="font-mono-data text-[10px] font-black uppercase tracking-[0.18em] text-[#10B981] mb-1">Value for investors</div>
                <p className="text-sm md:text-base text-ftext leading-snug">{w.core_messages.value}</p>
              </div>
            )}

            {/* Hero CTAs removed */}
          </div>
        </div>
      </div>
    </SectionShell>
  );
}

/* ------------------------------------------------------------------ Problem */
export function Problem({ data, common }) {
  const p = data.problem;
  const reduce = useReducedMotion();
  const impact = p.impact;
  const strip = p.evidence_strip;
  const [stripOpen, setStripOpen] = useState(false);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  return (
    <SectionShell id="problem" eyebrow={p.eyebrow} headline={p.headline} {...common}>
      {p.subhead && (
        <p className="text-base md:text-lg text-ftext2 max-w-4xl leading-relaxed mb-5">{p.subhead}</p>
      )}

      {/* Four-barrier grid (01 → 04) — HERO of this slide */}
      <div className="mb-3 flex items-center gap-2">
        <span className="font-mono-data text-xs font-black uppercase tracking-[0.18em] text-sky">The four barriers</span>
        <span className="flex-1 h-px bg-line" aria-hidden />
      </div>
      <div data-testid="problem-barriers" className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 auto-rows-fr mb-6">
        {p.cards.map((c, i) => (
          <motion.div
            key={c.key}
            data-testid={`problem-card-${c.key}`}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5, delay: reduce ? 0 : 0.15 + i * 0.1 }}
            className="group relative overflow-hidden border border-line bg-panel p-5 md:p-6 flex flex-col h-full transition-colors hover:border-sky"
          >
            <span className="absolute -top-3 right-3 font-heading font-black text-6xl md:text-7xl text-line/60 select-none pointer-events-none leading-none">
              {c.number || String(i + 1).padStart(2, "0")}
            </span>
            <div className="relative flex-1 flex flex-col">
              <div className="flex h-11 w-11 items-center justify-center border border-[#F87171]/40 bg-[#F87171]/10 mb-4">
                <Icon name={c.icon} className="w-5 h-5 text-[#F87171]" />
              </div>
              <h3 className="font-heading font-black text-lg md:text-xl text-ftext mb-2 leading-tight">{c.title}</h3>
              <p className="text-sm text-ftext2 leading-relaxed flex-1">{c.body}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Evidence strip — a measurable cost (collapsible on click) */}
      {strip && (
        <div data-testid="problem-evidence-strip" className="border border-line bg-panel/40 mb-5">
          <button
            type="button"
            data-testid="problem-evidence-toggle"
            onClick={() => setStripOpen((v) => !v)}
            aria-expanded={stripOpen}
            className="w-full flex items-center justify-between gap-3 px-4 md:px-5 py-3 hover:bg-panel/70 transition-colors"
          >
            <span className="flex items-center gap-2">
              <TrendingDown className="w-4 h-4 text-[#F87171]" />
              <span className="font-mono-data text-xs font-black uppercase tracking-[0.18em] text-[#F87171]">{strip.title}</span>
            </span>
            <span className="flex items-center gap-2">
              <span className="font-mono-data text-[10px] font-black uppercase tracking-[0.18em] text-ftext3">
                {stripOpen ? "Hide" : "Show details"}
              </span>
              <ChevronRight className={`w-4 h-4 text-ftext2 transition-transform ${stripOpen ? "rotate-90" : ""}`} />
            </span>
          </button>
          {stripOpen && (
            <div data-testid="problem-evidence-content" className="border-t border-line p-4 md:p-5">
              <div className="grid gap-3 md:grid-cols-3 auto-rows-fr mb-3">
                {strip.items.map((it) => (
                  <div key={it.key} data-testid={`evidence-${it.key}`} className="border border-line bg-panel p-3 flex flex-col h-full">
                    <div className="flex items-center gap-2 mb-1.5">
                      <Icon name={it.icon} className="w-4 h-4 text-sky" />
                      <span className="font-heading font-black text-sm text-ftext leading-tight">{it.label}</span>
                    </div>
                    <ul className="text-xs text-ftext2 leading-snug space-y-1 flex-1">
                      {it.bullets.map((b, bi) => (
                        <li key={bi} className="flex items-start gap-1.5">
                          <span className="text-sky mt-0.5">·</span>
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                    {it.footnote && (
                      <p className="mt-2 text-[10px] italic text-ftext3 leading-snug">{it.footnote}</p>
                    )}
                  </div>
                ))}
              </div>
              {strip.conclusion && (
                <p className="font-heading font-bold text-sm md:text-base text-ftext leading-snug border-t border-line pt-3">{strip.conclusion}</p>
              )}
            </div>
          )}
        </div>
      )}

      {/* CIO callout removed per request */}

      {p.bottom_line && (
        <div data-testid="problem-bottom-line" className="mt-2 border-l-4 border-sky bg-panel/60 px-4 py-3 max-w-5xl">
          <p className="font-heading font-bold text-base md:text-lg text-ftext leading-snug">
            {p.bottom_line}
          </p>
        </div>
      )}

      {/* Sources — collapsible */}
      {((p.sources && p.sources.length > 0) || (impact && impact.source)) && (
        <div data-testid="problem-sources" className="mt-4 border border-line/60 bg-panel/30 max-w-5xl">
          <button
            type="button"
            data-testid="problem-sources-toggle"
            onClick={() => setSourcesOpen((v) => !v)}
            aria-expanded={sourcesOpen}
            className="w-full flex items-center justify-between gap-3 px-3 py-2 hover:bg-panel/70 transition-colors"
          >
            <span className="font-mono-data text-[10px] font-black uppercase tracking-[0.18em] text-ftext3">Sources & citations</span>
            <ChevronRight className={`w-3.5 h-3.5 text-ftext3 transition-transform ${sourcesOpen ? "rotate-90" : ""}`} />
          </button>
          {sourcesOpen && (
            <div data-testid="problem-sources-content" className="border-t border-line/60 px-3 py-2 text-[11px] text-ftext3 leading-snug space-y-1">
              {p.sources && p.sources.length > 0 && (
                <p>
                  <span className="font-mono-data font-black uppercase tracking-[0.14em] text-ftext3">Primary · </span>
                  {p.sources.map((s, i) => (
                    <span key={i}>
                      {s.url ? (
                        <a href={s.url} target="_blank" rel="noopener noreferrer" className="underline hover:text-sky transition-colors">{s.label}</a>
                      ) : (
                        <span>{s.label}</span>
                      )}
                      {i < p.sources.length - 1 && <span>; </span>}
                    </span>
                  ))}
                </p>
              )}
              {impact && impact.source && (
                <p>
                  <span className="font-mono-data font-black uppercase tracking-[0.14em] text-ftext3">Supporting · </span>
                  <span>{impact.number} — {impact.label}. </span>
                  <a href="https://new.abb.com/news/detail/107660/abb-survey-reveals-unplanned-downtime-costs-125-000-per-hour" target="_blank" rel="noopener noreferrer" className="underline hover:text-sky transition-colors">
                    {impact.source}
                  </a>
                  {impact.caveat && <span className="italic ml-1">· {impact.caveat}</span>}
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </SectionShell>
  );
}

/* ------------------------------------------------------------------ Solution */
export function Solution({ data, common }) {
  const s = data.solution;
  const reduce = useReducedMotion();
  const rise = (i = 0) => ({
    initial: { opacity: 0, y: 16 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.5, delay: reduce ? 0 : i * 0.08 },
  });
  return (
    <SectionShell id="solution" eyebrow={s.eyebrow} headline={s.headline} {...common}>
      {s.subhead && (
        <div data-testid="solution-handoff" className="mb-5 inline-flex items-center gap-2 border-l-4 border-sky bg-panel/60 px-4 py-2.5">
          <span className="font-heading font-bold text-base md:text-lg text-ftext leading-snug">{s.subhead}</span>
        </div>
      )}

      {/* HERO problem → response grid (2x2) mirrors Problem 01→04 */}
      <div data-testid="solution-problem-mapping" className="grid gap-4 md:grid-cols-2 auto-rows-fr">
        {s.problem_mapping.map((m, i) => (
          <motion.div
            key={i}
            {...rise(i)}
            data-testid={`solution-mapping-${i}`}
            className="relative overflow-hidden border border-line bg-panel p-5 md:p-6 flex flex-col h-full hover:border-sky transition-colors"
          >
            <span className="absolute -top-3 right-3 font-heading font-black text-6xl md:text-7xl text-line/60 select-none pointer-events-none leading-none">
              {m.number || String(i + 1).padStart(2, "0")}
            </span>
            <div className="relative flex items-start gap-4 flex-1 flex-col sm:flex-row">
              <div className="flex h-12 w-12 items-center justify-center border border-sky/40 bg-sky/10 shrink-0">
                <Icon name={m.icon || "AlertTriangle"} className="w-6 h-6 text-sky" />
              </div>
              <div className="flex-1 min-w-0 flex flex-col h-full">
                <div className="font-mono-data text-[10px] font-black uppercase tracking-[0.18em] text-ftext3">{m.problem_tag || `Problem ${i + 1}`}</div>
                <div className="font-heading font-black text-lg md:text-xl text-ftext leading-tight mt-1">{m.problem}</div>
                <p className="text-sm md:text-base text-ftext2 leading-snug mt-2">{m.response}</p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {s.bottom_line && (
        <div data-testid="solution-bottom-line" className="mt-6 border-l-4 border-[#10B981] bg-panel/60 px-4 py-3 max-w-5xl">
          <p className="font-heading font-bold text-base md:text-lg text-ftext leading-snug">{s.bottom_line}</p>
        </div>
      )}
    </SectionShell>
  );
}

/* --------------------------------------------------------- Architecture (SVG diagram mirroring streamline360.ai) */
export function Architecture({ data, common }) {
  const s = data.solution;
  return (
    <SectionShell id="architecture" eyebrow="The Architecture" headline={s.architecture_headline || "How it works"} {...common}>
      {s.architecture_subhead && (
        <p className="text-sm md:text-base text-ftext2 max-w-4xl leading-relaxed mb-4">{s.architecture_subhead}</p>
      )}

      <ArchitectureDiagram />

      {/* Legend chip row */}
      <div data-testid="arch-legend" className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-1.5 text-[11px] md:text-xs">
        <span className="inline-flex items-center gap-1.5 text-ftext3">
          <span className="w-2 h-2 rounded-full bg-sky" />
          Raw telemetry · inside the plant
        </span>
        <span className="inline-flex items-center gap-1.5 text-ftext3">
          <span className="w-2 h-2 rounded-full bg-[#10B981]" />
          Abstracted metadata · what agents reason over
        </span>
        <span className="inline-flex items-center gap-1.5 text-ftext3">
          <span className="font-mono-data text-[#F87171] font-black">✕</span>
          Raw data records · blocked to cloud LLMs
        </span>
      </div>

      {/* Connectors badge row */}
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        <span className="font-mono-data text-[10px] font-black uppercase tracking-[0.14em] text-ftext3">Connectors</span>
        {(s.connectors || ["OPC-UA", "MQTT", "Native Historian APIs"]).map((c) => (
          <span key={c} className="font-mono-data text-[11px] font-bold uppercase tracking-wider text-ftext2 border border-line2 bg-ink px-2 py-0.5">{c}</span>
        ))}
      </div>
    </SectionShell>
  );
}

/* --------------------------------------------------------- Trust & Provenance (split from Solution) */
export function TrustProvenance({ data, common }) {
  const s = data.solution;
  const ec = s.evidence_chain;
  if (!ec) return null;
  return (
    <SectionShell id="trust" eyebrow="Trust & Provenance" headline="Every answer, sourced." {...common}>
      <p className="text-base md:text-lg text-ftext2 max-w-3xl leading-relaxed mb-8">Streamline360 answers are constructed only from retrieved plant records — each output linked to its origin so operators, engineers and auditors can verify the reasoning chain.</p>
      <div data-testid="trust-evidence-chain" className="border border-line bg-panel p-5">
        <div className="flex items-center gap-2 mb-4">
          <BadgeCheck className="w-4 h-4 text-sky" />
          <div className="font-mono-data text-xs font-black uppercase tracking-[0.18em] text-ftext">{ec.title}</div>
        </div>
        <div className="grid gap-3 md:grid-cols-4">
          {ec.steps.map((st, i) => (
            <div key={st.step} className="relative border border-line2 bg-ink px-4 py-4">
              <span className="font-mono-data text-[10px] font-black uppercase tracking-[0.18em] text-sky">Step {i + 1}</span>
              <div className="font-heading font-black text-lg text-ftext leading-tight mt-1">{st.step}</div>
              <p className="text-sm text-ftext2 mt-1.5 leading-relaxed">{st.detail}</p>
              {i < ec.steps.length - 1 && (
                <span aria-hidden className="hidden md:block absolute -right-2.5 top-1/2 -translate-y-1/2 font-heading font-black text-sky/70">→</span>
              )}
            </div>
          ))}
        </div>
        {ec.note && (
          <p className="text-xs text-ftext3 italic mt-4 leading-relaxed">{ec.note}</p>
        )}
      </div>
    </SectionShell>
  );
}
export function Capabilities({ data, common }) {
  const s = data.solution;
  const vp = s.value_progression;
  const reduce = useReducedMotion();
  const rise = (i = 0) => ({
    initial: { opacity: 0, y: 16 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.5, delay: reduce ? 0 : i * 0.1 },
  });
  return (
    <SectionShell id="capabilities" eyebrow="What the agents deliver" headline="What the agents do" {...common}>
      <p className="text-base md:text-lg text-ftext2 max-w-4xl leading-relaxed mb-6">Abstracted metadata + Knowledge Graph → agents that do the work of a plant analyst, around the clock.</p>

      {vp && (
        <div data-testid="value-progression">
          <div className="grid gap-4 md:grid-cols-2 auto-rows-fr">
            {vp.stages.map((st, i) => (
              <motion.div
                key={st.key}
                {...rise(i)}
                data-testid={`value-stage-${st.key}`}
                className="group border bg-panel p-5 md:p-6 flex flex-col h-full transition-colors border-line hover:border-sky"
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="flex h-12 w-12 items-center justify-center border shrink-0 border-sky/40 bg-sky/10 group-hover:bg-sky/15">
                    <Icon name={st.icon} className="w-6 h-6 text-sky" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono-data text-[10px] font-black uppercase tracking-[0.18em] text-sky mb-0.5">0{i + 1}</div>
                    <div className="font-heading font-black text-xl md:text-2xl text-ftext leading-tight">{st.label}</div>
                  </div>
                </div>
                <p className="text-sm md:text-base text-ftext2 leading-relaxed flex-1">{st.body}</p>
                {st.agents && (
                  <div className="mt-4 pt-3 border-t border-line">
                    <div className="font-mono-data text-[10px] font-black uppercase tracking-[0.18em] text-ftext3 mb-1.5">Agents</div>
                    <div className="flex flex-wrap gap-1.5">
                      {st.agents.map((a) => (
                        <span key={a} className="font-mono-data text-[10px] font-bold uppercase tracking-wider text-ftext border border-line2 bg-ink px-2 py-1">
                          {a}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      )}

      <div data-testid="solution-outputs" className="hidden">
        <div className="font-mono-data text-xs font-black uppercase tracking-[0.18em] text-ftext2 mb-2">What the agents deliver</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {s.outputs.map((o, i) => (
            <motion.div key={o.name} {...rise(i)} className="border border-line bg-panel p-3 hover:border-sky transition-colors">
              <div className="flex h-8 w-8 items-center justify-center border border-[#10B981]/40 bg-[#10B981]/10 mb-2">
                <Icon name={o.icon} className="w-4 h-4 text-[#10B981]" />
              </div>
              <div className="font-heading font-bold text-sm text-ftext leading-tight">{o.name}</div>
              {o.desc && <p className="text-[11px] text-ftext2 mt-1 leading-snug">{o.desc}</p>}
            </motion.div>
          ))}
        </div>
      </div>
    </SectionShell>
  );
}

/* --------------------------------------------------------- Maturity */
export function Maturity({ data, common }) {
  const s = data.solution;
  const reduce = useReducedMotion();
  const rise = (i = 0) => ({
    initial: { opacity: 0, y: 16 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.5, delay: reduce ? 0 : i * 0.1 },
  });
  if (!s.maturity) return null;
  return (
    <SectionShell id="maturity" eyebrow="Digital maturity" headline={s.maturity.title} {...common}>
      <p className="text-sm md:text-base text-ftext2 mb-6 max-w-3xl">{s.maturity.subhead}</p>

      <div className="border border-line bg-panel p-5 md:p-6" data-testid="solution-maturity">
        <div className="mb-3 flex items-center gap-2">
          <span className="font-mono-data text-xs md:text-sm font-black uppercase tracking-wider text-ftext">Agents required</span>
          <span className="font-heading font-black text-sky text-lg">↑</span>
        </div>
        <div className="flex gap-4 md:gap-5">
          <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            {s.maturity.levels.map((lvl, i) => {
              const heights = ["min-h-[160px]", "min-h-[220px]", "min-h-[280px]"];
              return (
                <motion.div
                  key={lvl.key}
                  {...rise(i)}
                  data-testid={`maturity-${lvl.key}`}
                  className={`relative border border-line2 bg-ink p-4 flex flex-col ${heights[i]}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-mono-data text-[10px] font-black uppercase tracking-[0.18em] text-sky">Level {i + 1}</span>
                    {lvl.stage && (
                      <>
                        <span className="font-mono-data text-[10px] font-bold uppercase tracking-wider text-ftext3">·</span>
                        <span data-testid={`maturity-stage-${lvl.key}`} className="font-mono-data text-[10px] font-black uppercase tracking-wider text-[#10B981] border border-[#10B981]/40 bg-[#10B981]/10 px-1.5 py-0.5">
                          {lvl.stage}
                        </span>
                      </>
                    )}
                    <span className="font-mono-data text-[10px] font-bold uppercase tracking-wider text-ftext3">·</span>
                    <span className="font-mono-data text-[10px] font-bold uppercase tracking-wider text-ftext3">{lvl.agents.length} agent{lvl.agents.length > 1 ? "s" : ""}</span>
                  </div>
                  <div className="font-heading font-black text-xl md:text-2xl text-ftext leading-tight">{lvl.label}</div>
                  <p className="text-xs md:text-sm text-ftext2 mt-1 leading-relaxed">{lvl.caption}</p>
                  <div className="mt-4 space-y-2">
                    {lvl.agents.map((a) => {
                      const isFlag = lvl.flagship && a.name === lvl.flagship;
                      return (
                        <div
                          key={a.name}
                          className={`relative flex items-center gap-2 border px-2.5 py-2 ${isFlag ? "border-sky bg-sky/10" : "border-line bg-panel"}`}
                        >
                          <Icon name={a.icon} className={`w-4 h-4 shrink-0 ${isFlag ? "text-sky" : "text-sky"}`} />
                          <span className={`text-xs md:text-sm leading-tight ${isFlag ? "font-black text-ftext" : "font-semibold text-ftext"}`}>{a.name}</span>
                          {isFlag && (
                            <span className="ml-auto font-mono-data text-[9px] font-black uppercase tracking-[0.14em] text-sky border border-sky/60 bg-sky/10 px-1.5 py-0.5 shrink-0">Flagship</span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
        <div className="mt-5 md:ml-12 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="font-heading font-black text-sky text-lg">→</span>
            <span className="font-mono-data text-xs md:text-sm font-black uppercase tracking-[0.22em] text-ftext">Digital maturity</span>
          </div>
          <span data-testid="maturity-progression" className="font-mono-data text-[11px] md:text-xs font-bold uppercase tracking-wider text-ftext2 hidden md:inline">
            {(s.maturity.progression || ["Basic", "Intermediate", "Advanced"]).join("  →  ")}
          </span>
        </div>
      </div>
    </SectionShell>
  );
}

/* ------------------------------------------------------------------ Traction */
export function Traction({ data, common }) {
  const t = data.traction;
  return (
    <SectionShell id="traction" eyebrow={t.eyebrow} headline={t.headline} {...common}>
      {t.subhead && (
        <p className="text-lg md:text-2xl font-bold text-ftext max-w-3xl leading-snug mb-6">{t.subhead}</p>
      )}

      {/* Hero metrics */}
      {t.hero_metrics && (
        <div data-testid="traction-hero" className="grid gap-3 md:grid-cols-3 mb-6">
          {t.hero_metrics.map((h) => (
            <div key={h.label} className="border-l-4 border-sky bg-panel px-5 py-4">
              <div className="font-heading font-black text-4xl md:text-5xl text-ftext leading-none">{h.value}</div>
              <div className="font-mono-data text-[11px] font-bold uppercase tracking-[0.14em] text-ftext mt-2">{h.label}</div>
              {h.sub && <div className="text-xs text-ftext3 mt-1">{h.sub}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Expansion ladder */}
      {t.expansion_ladder && (
        <div data-testid="traction-expansion-ladder" className="border border-line bg-panel p-5 mb-6">
          <div className="flex items-center justify-between gap-3 mb-3">
            <div className="font-mono-data text-xs font-black uppercase tracking-[0.18em] text-ftext">{t.expansion_ladder.title}</div>
            <span className="font-mono-data text-[10px] font-bold uppercase tracking-wider text-ftext3 border border-line2 px-1.5 py-0.5">Illustrative</span>
          </div>
          <div className="grid gap-2 md:grid-cols-4">
            {t.expansion_ladder.steps.map((s, i) => (
              <div key={i} className="relative border border-line2 bg-ink p-3">
                <div className="font-mono-data text-[9px] font-black uppercase tracking-wider text-sky">Step {i + 1}</div>
                <div className="font-heading font-black text-xl text-ftext leading-tight mt-1">{s.plants} plant{s.plants === 1 ? "" : "s"}</div>
                <div className="font-mono-data text-xs font-black text-[#10B981] mt-0.5">{s.arr}</div>
                <div className="text-xs text-ftext2 mt-1 leading-snug">{s.stage}</div>
                {i < t.expansion_ladder.steps.length - 1 && (
                  <span aria-hidden className="hidden md:block absolute -right-2 top-1/2 -translate-y-1/2 font-heading font-black text-sky">→</span>
                )}
              </div>
            ))}
          </div>
          <p className="text-xs text-ftext3 italic mt-3 leading-relaxed">{t.expansion_ladder.note}</p>
        </div>
      )}
      {/* Paid pilots table moved to next slide */}
    </SectionShell>
  );
}

/* --------------------------------------------------------- Paid Pilots (split from Traction) */
export function PaidPilots({ data, common }) {
  const t = data.traction;
  return (
    <SectionShell id="pilots" eyebrow="Paid Pilots" headline="Paying customers" {...common}>
      <p className="text-base md:text-lg text-ftext2 max-w-3xl leading-relaxed mb-6">Four paying pilots — 77 plants of rollout potential across four groups.</p>
      <div className="grid gap-5 md:grid-cols-2" data-testid="paid-pilots-grid">
        {t.pilots.map((pl) => (
          <PilotCard key={pl.key} pilot={pl} />
        ))}
      </div>
      {t.card_disclaimer && (
        <p className="mt-4 text-[11px] text-ftext3 italic leading-snug max-w-4xl">{t.card_disclaimer}</p>
      )}
    </SectionShell>
  );
}

function PilotCard({ pilot }) {
  const [open, setOpen] = useState(false);
  const [imgErr, setImgErr] = useState(false);
  const logoSrc = pilot.domain ? `https://logo.clearbit.com/${pilot.domain}` : null;
  const faviconFallback = pilot.domain ? `https://www.google.com/s2/favicons?domain=${pilot.domain}&sz=256` : null;
  const [currentSrc, setCurrentSrc] = useState(logoSrc);
  const handleImgErr = () => {
    if (currentSrc === logoSrc && faviconFallback) {
      setCurrentSrc(faviconFallback);
    } else {
      setImgErr(true);
    }
  };
  const maturityLevel = pilot.maturity ? pilot.maturity.charAt(0).toUpperCase() + pilot.maturity.slice(1) : null;
  return (
    <div
      data-testid={`pilot-${pilot.key}`}
      className="border border-line bg-panel transition-colors hover:border-sky/50"
    >
      {/* Centerpiece: big logo on white */}
      <div className="flex items-center justify-center bg-white h-28 border-b border-line">
        {currentSrc && !imgErr ? (
          <img
            src={currentSrc}
            onError={handleImgErr}
            alt={pilot.name}
            className="max-h-20 max-w-[65%] object-contain"
          />
        ) : (
          <div className="flex items-center gap-3">
            <Icon name={pilot.icon} className="w-10 h-10 text-ink" />
            <span className="font-heading font-black text-2xl text-ink">{pilot.name}</span>
          </div>
        )}
      </div>

      {/* Headline area: application / workflow name */}
      <div className="px-5 pt-4 pb-3">
        <div className="flex items-center justify-between gap-2 mb-2">
          <div className="font-mono-data text-[10px] font-black uppercase tracking-[0.18em] text-sky">{pilot.name}</div>
          <div className="flex items-center gap-1.5">
            <span className="font-mono-data text-[10px] uppercase tracking-wider border border-line2 px-1.5 py-0.5 text-ftext2">{pilot.stage}</span>
            {maturityLevel && (
              <span
                data-testid={`pilot-${pilot.key}-maturity`}
                className="font-mono-data text-[9px] font-black uppercase tracking-[0.16em] border border-sky bg-sky/10 text-sky px-1.5 py-0.5"
              >
                {maturityLevel}
              </span>
            )}
          </div>
        </div>
        <h3 className="font-heading font-black text-lg md:text-xl text-ftext leading-tight">{pilot.workflow}</h3>
        <div className="font-mono-data text-[11px] uppercase tracking-wider text-ftext3 mt-1">{pilot.sector}</div>
        {(pilot.started || pilot.target_kpi || pilot.result || pilot.next_step) && (
          <div data-testid={`pilot-${pilot.key}-meta`} className="mt-3 pt-3 border-t border-line grid grid-cols-2 gap-x-3 gap-y-1.5 text-[11px]">
            {pilot.started && (<><div className="font-mono-data uppercase tracking-wider text-ftext3">Started</div><div className="text-ftext2">{pilot.started}</div></>)}
            {pilot.target_kpi && (<><div className="font-mono-data uppercase tracking-wider text-ftext3">Target KPI</div><div className="text-ftext2">{pilot.target_kpi}</div></>)}
            {pilot.result && (<><div className="font-mono-data uppercase tracking-wider text-ftext3">Result so far</div><div className="text-ftext2">{pilot.result}</div></>)}
            {pilot.next_step && (<><div className="font-mono-data uppercase tracking-wider text-ftext3">Next step</div><div className="text-ftext2">{pilot.next_step}</div></>)}
          </div>
        )}
      </div>

      {/* Collapsible details */}
      <div className="border-t border-line">
        <button
          type="button"
          data-testid={`pilot-${pilot.key}-toggle`}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="w-full flex items-center justify-between px-5 py-2.5 font-mono-data text-[11px] font-black uppercase tracking-[0.18em] text-ftext2 hover:text-sky transition-colors"
        >
          <span>{open ? "Hide details" : "Show details"}</span>
          <ChevronRight className={`w-4 h-4 transition-transform ${open ? "rotate-90" : ""}`} />
        </button>
        {open && (
          <div data-testid={`pilot-${pilot.key}-details`} className="px-5 pb-4 space-y-3 text-xs border-t border-line pt-3">
            <div>
              <div className="font-mono-data text-[10px] uppercase tracking-wider text-ftext3">User value</div>
              <p className="text-ftext2 mt-1 leading-snug">{pilot.value}</p>
            </div>
            <div>
              <div className="font-mono-data text-[10px] uppercase tracking-wider text-[#F59E0B]">Status</div>
              <p className="text-ftext mt-1 leading-snug flex items-start gap-1.5">
                <ChevronRight className="w-3.5 h-3.5 text-sky mt-0.5 shrink-0" />
                {pilot.status_label}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ Moat */
export function Moat({ data, common }) {
  const m = data.moat;
  return (
    <SectionShell id="moat" eyebrow={m.eyebrow} headline={m.headline} {...common}>
      {m.subhead && (
        <p className="text-base md:text-lg text-ftext2 max-w-4xl leading-relaxed mb-6">{m.subhead}</p>
      )}
      {m.layers && (
        <div data-testid="moat-layers" className="grid gap-3 md:grid-cols-2 auto-rows-fr">
          {m.layers.map((l) => {
            const tone = l.label === "Demonstrated"
              ? "border-[#10B981]/50 bg-[#10B981]/10 text-[#10B981]"
              : l.label === "In deployment"
              ? "border-sky/50 bg-sky/10 text-sky"
              : "border-line2 text-ftext2";
            return (
              <div key={l.key} data-testid={`moat-layer-${l.key}`} className="relative border border-line bg-panel p-4 flex items-start gap-3 h-full">
                <div className="flex h-10 w-10 items-center justify-center border border-sky/40 bg-sky/10 shrink-0">
                  <Icon name={l.icon} className="w-5 h-5 text-sky" />
                </div>
                <div className="flex-1 min-w-0 flex flex-col h-full">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <div className="font-heading font-black text-base md:text-lg text-ftext leading-tight">{l.title}</div>
                    <span className={`font-mono-data text-[10px] font-black uppercase tracking-[0.14em] border px-1.5 py-0.5 ${tone}`}>{l.label}</span>
                  </div>
                  <p className="text-xs md:text-sm text-ftext2 leading-snug">{l.body}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </SectionShell>
  );
}

/* --------------------------------------------------------- Why Streamline360 wins (new slide B) */
export function WhyWeWin({ data, common }) {
  const w = data.why_we_win;
  if (!w) return null;
  return (
    <SectionShell id="why_we_win" eyebrow={w.eyebrow} headline={w.headline} {...common}>
      {w.subhead && (
        <p className="text-sm md:text-base text-ftext2 max-w-4xl leading-relaxed mb-4">{w.subhead}</p>
      )}

      {/* Comparison table */}
      <div data-testid="why-we-win-comparison" className="border border-line bg-panel mb-4 overflow-x-auto">
        <table className="w-full text-xs md:text-sm">
          <thead>
            <tr className="border-b border-line bg-ink font-mono-data text-[10px] uppercase tracking-wider text-ftext3">
              {w.comparison_columns.map((col, i) => (
                <th key={i} className={`text-left px-3 py-2 ${i === 0 ? "w-[18%]" : "w-[41%]"}`}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {w.comparison_rows.map((row, i) => (
              <tr key={i} data-testid={`why-we-win-row-${i}`} className="border-b border-line/60 last:border-0">
                <td className="px-3 py-2 font-heading font-bold text-ftext leading-snug align-top">{row.dim}</td>
                <td className="px-3 py-2 text-ftext3 leading-snug align-top">{row.generic}</td>
                <td className="px-3 py-2 text-sky leading-snug align-top">{row.s360}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Causal chain */}
      {w.causal_chain && (
        <div data-testid="why-we-win-chain" className="border border-line bg-panel p-3 mb-4">
          <div className="font-mono-data text-[11px] font-black uppercase tracking-[0.18em] text-sky mb-2">Causal chain</div>
          <div className="flex flex-wrap items-center gap-1.5">
            {w.causal_chain.map((step, i) => (
              <React.Fragment key={i}>
                <span className="font-mono-data text-[11px] font-bold uppercase tracking-wider text-ftext border border-line2 bg-ink px-2.5 py-1">
                  {step}
                </span>
                {i < w.causal_chain.length - 1 && (
                  <span aria-hidden className="font-heading font-black text-sky">→</span>
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Benchmark metrics with diligence tag */}
      {w.benchmark && (
        <div data-testid="why-we-win-benchmark" className="border border-line bg-panel p-4">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
            <div className="font-mono-data text-[11px] font-black uppercase tracking-[0.18em] text-sky">{w.benchmark.title}</div>
            <span className="font-mono-data text-[10px] font-bold uppercase tracking-wider text-ftext3 border border-line2 px-1.5 py-0.5">
              {w.benchmark.diligence_tag}
            </span>
          </div>
          <div className="grid gap-3 md:grid-cols-3">
            {w.benchmark.metrics.map((mt, i) => (
              <div key={i} data-testid={`why-we-win-metric-${i}`} className="border border-line2 bg-ink p-3">
                <div className="font-mono-data text-[10px] uppercase tracking-wider text-ftext3">{mt.label}</div>
                <div className="font-mono-data font-bold text-2xl text-sky mt-1">{mt.value}</div>
                <p className="text-[11px] text-ftext2 leading-snug mt-1">{mt.compare}</p>
              </div>
            ))}
          </div>
          <p className="text-[11px] text-ftext3 italic mt-3 leading-snug">{w.benchmark.note}</p>
        </div>
      )}
    </SectionShell>
  );
}

/* --------------------------------------------------------- End-user value (3-audience card grid) */
export function EndUserValue({ data, common }) {
  const e = data.end_user_value;
  if (!e) return null;
  return (
    <SectionShell id="end_user_value" eyebrow={e.eyebrow} headline={e.headline} {...common}>
      {e.subhead && (
        <p className="text-base md:text-lg text-ftext2 max-w-4xl leading-relaxed mb-6">{e.subhead}</p>
      )}
      <div data-testid="end-user-value-grid" className="grid gap-4 md:grid-cols-3">
        {e.cards.map((c) => (
          <div key={c.key} data-testid={`end-user-card-${c.key}`} className="group border border-line bg-panel p-5 md:p-6 hover:border-sky transition-colors flex flex-col">
            <div className="flex items-center gap-3 mb-2">
              <div className="flex h-12 w-12 items-center justify-center border border-sky/40 bg-sky/10 shrink-0 group-hover:bg-sky/15">
                <Icon name={c.icon} className="w-6 h-6 text-sky" />
              </div>
              <div className="font-heading font-black text-xl md:text-2xl text-ftext leading-tight">{c.role}</div>
            </div>
            {c.audience && (
              <div className="font-mono-data text-[10px] font-bold uppercase tracking-[0.14em] text-ftext3 mb-4 leading-snug">{c.audience}</div>
            )}
            <div className="space-y-3 flex-1">
              <div>
                <div className="font-mono-data text-[10px] font-black uppercase tracking-[0.18em] text-[#F87171] mb-1">Problem</div>
                <p className="text-sm md:text-base text-ftext2 leading-snug">{c.from}</p>
              </div>
              <div className="h-px bg-line" aria-hidden />
              <div>
                <div className="font-mono-data text-[10px] font-black uppercase tracking-[0.18em] text-[#10B981] mb-1">Our value</div>
                <p className="text-sm md:text-base text-ftext leading-snug font-semibold">{c.to}</p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </SectionShell>
  );
}

/* --------------------------------------------------------- Competition — buyer dimensions (new split) */
export function CompetitionDimensions({ data, common }) {
  const c = data.competition_dimensions;
  if (!c) return null;
  return (
    <SectionShell id="competition_dimensions" eyebrow={c.eyebrow} headline={c.headline} {...common}>
      {c.subhead && (
        <p className="text-sm md:text-base text-ftext2 max-w-4xl leading-relaxed mb-4">{c.subhead}</p>
      )}
      <div data-testid="competition-dimensions-table" className="border border-line bg-panel overflow-x-auto mb-4">
        <table className="w-full text-xs md:text-sm">
          <thead>
            <tr className="border-b border-line bg-ink font-mono-data text-[10px] uppercase tracking-wider text-ftext3">
              {c.columns.map((col, i) => (
                <th key={i} className={`text-left px-3 py-2 ${i === 0 ? "w-[22%]" : "w-[39%]"}`}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {c.rows.map((row, i) => (
              <tr key={i} data-testid={`competition-dim-${i}`} className="border-b border-line/60 last:border-0">
                <td className="px-3 py-2 font-heading font-bold text-ftext leading-snug align-top">{row.dim}</td>
                <td className="px-3 py-2 text-ftext3 leading-snug align-top">{row.incumbent}</td>
                <td className="px-3 py-2 text-sky leading-snug align-top">{row.s360}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {c.acknowledgement && (
        <div data-testid="competition-dim-ack" className="border-l-4 border-[#F59E0B] bg-panel px-4 py-3">
          <div className="font-mono-data text-[11px] font-black uppercase tracking-[0.18em] text-[#F59E0B] mb-1">{c.acknowledgement.title}</div>
          <p className="text-xs md:text-sm text-ftext2 leading-snug">{c.acknowledgement.body}</p>
        </div>
      )}
    </SectionShell>
  );
}

/* ------------------------------------------------------------------ Competition (Industrial-AI positioning grid) */
export function Competition({ data, common }) {
  const c = data.competition;
  const [active, setActive] = useState(null);
  if (!c) return null;
  const quadrantMeta = {
    leaders: { name: "Leaders", tone: "text-[#10B981]", dot: "bg-[#10B981]", ring: "ring-[#10B981]/50" },
    challengers: { name: "Challengers", tone: "text-sky", dot: "bg-sky", ring: "ring-sky/50" },
    visionaries: { name: "Innovators", tone: "text-[#F59E0B]", dot: "bg-[#F59E0B]", ring: "ring-[#F59E0B]/50" },
    niche: { name: "Niche Players", tone: "text-ftext2", dot: "bg-ftext3", ring: "ring-line2" },
  };
  const hovered = active ? c.players.find((p) => p.key === active) : null;

  return (
    <SectionShell id="competition" eyebrow={c.eyebrow} headline={c.headline} {...common}>
      <p className="text-xs md:text-sm text-ftext2 max-w-3xl leading-relaxed mb-3">{c.subhead}</p>

      <div className="grid gap-3 lg:grid-cols-[2.1fr_1fr] items-stretch">
        {/* Quadrant plot — cleaner, bigger pills */}
        <div className="border border-line bg-panel p-3">
          <div className="relative w-full" style={{ paddingBottom: "62%" }}>
            <div data-testid="competition-plot" className="absolute inset-0 bg-ink border border-line2 overflow-hidden">
              {/* Grid quadrant fills */}
              <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 pointer-events-none">
                <div className="border-r border-b border-line2/60 bg-sky/[0.03]" />
                <div className="border-b border-line2/60 bg-[#10B981]/[0.03]" />
                <div className="border-r border-line2/60 bg-line/20" />
                <div className="bg-[#F59E0B]/[0.03]" />
              </div>
              {/* Quadrant labels */}
              {c.quadrants.map((q) => {
                const posClass = {
                  tr: "top-3 right-3 text-right",
                  tl: "top-3 left-3 text-left",
                  br: "bottom-3 right-3 text-right",
                  bl: "bottom-3 left-3 text-left",
                }[q.position];
                const meta = quadrantMeta[q.key] || quadrantMeta.niche;
                return (
                  <div key={q.key} className={`absolute ${posClass} pointer-events-none z-20`}>
                    <span className={`font-mono-data text-[9px] md:text-[13px] font-black uppercase tracking-[0.22em] ${meta.tone} px-1.5 py-0.5 md:px-2 md:py-1 bg-ink/60 backdrop-blur-sm border border-current/30`}>
                      {q.label}
                    </span>
                  </div>
                );
              })}
              {/* Center crosshair */}
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute left-1/2 top-0 bottom-0 w-px bg-line2/60" />
                <div className="absolute top-1/2 left-0 right-0 h-px bg-line2/60" />
              </div>
              {/* Players */}
              {c.players.map((p) => {
                const meta = quadrantMeta[p.quadrant] || quadrantMeta.niche;
                const isMe = p.highlight;
                const isActive = active === p.key;
                return (
                  <button
                    key={p.key}
                    type="button"
                    data-testid={`comp-player-${p.key}`}
                    onMouseEnter={() => setActive(p.key)}
                    onMouseLeave={() => setActive(null)}
                    onFocus={() => setActive(p.key)}
                    onBlur={() => setActive(null)}
                    aria-label={`${p.full_name || p.name} — ${meta.name}`}
                    style={{ left: `${p.x}%`, bottom: `${p.y}%`, transform: "translate(-50%, 50%)" }}
                    className={`absolute inline-flex items-center gap-1.5 md:gap-2 rounded-full px-2 md:px-3.5 py-1 md:py-1.5 border transition-all cursor-pointer ${
                      isMe
                        ? "bg-sky text-primary-foreground border-sky shadow-[0_0_0_6px_rgba(56,189,248,0.28)] font-black z-30 scale-110"
                        : `bg-panel/95 border-line2 text-ftext hover:border-sky hover:scale-105 ${isActive ? "ring-2 " + meta.ring + " scale-105 z-20" : "z-10"}`
                    }`}
                  >
                    <span className={`w-1.5 h-1.5 md:w-2 md:h-2 rounded-full ${isMe ? "bg-white" : meta.dot}`} />
                    <span className="font-mono-data text-[9px] md:text-[13px] font-bold uppercase tracking-wider whitespace-nowrap">{p.name}</span>
                  </button>
                );
              })}
            </div>
          </div>
          {/* Axes */}
          <div className="mt-2 flex items-center justify-between font-mono-data text-[9px] font-bold uppercase tracking-wider text-ftext3">
            <span>← {c.axes.x_min}</span>
            <span className="text-ftext2 text-center">{c.axes.x_label}</span>
            <span>{c.axes.x_max} →</span>
          </div>
          <div className="mt-0.5 font-mono-data text-[9px] text-ftext3 text-center">Y · {c.axes.y_label} ({c.axes.y_min} ↑ {c.axes.y_max})</div>
        </div>

        {/* Hover detail card */}
        <div data-testid="comp-detail" className="border border-line bg-panel p-4 flex flex-col min-h-[220px]">
          {hovered ? (
            <>
              <div className="flex items-center gap-2 mb-1">
                <span className={`w-2 h-2 rounded-full ${quadrantMeta[hovered.quadrant].dot}`} />
                <span className={`font-mono-data text-[10px] font-black uppercase tracking-[0.18em] ${quadrantMeta[hovered.quadrant].tone}`}>{quadrantMeta[hovered.quadrant].name}</span>
                <span className="font-mono-data text-[10px] font-bold uppercase tracking-wider text-ftext3">· {hovered.category}</span>
              </div>
              <div className="font-heading font-black text-lg text-ftext leading-tight mb-3">{hovered.full_name || hovered.name}</div>
              <div className="space-y-2.5">
                <div>
                  <div className="font-mono-data text-[10px] font-black uppercase tracking-wider text-[#10B981] mb-0.5">Strengths</div>
                  <p className="text-xs text-ftext leading-relaxed">{hovered.strengths}</p>
                </div>
                <div>
                  <div className="font-mono-data text-[10px] font-black uppercase tracking-wider text-[#F87171] mb-0.5">Gap vs. Streamline360</div>
                  <p className="text-xs text-ftext2 leading-relaxed">{hovered.gap}</p>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="font-mono-data text-[10px] font-black uppercase tracking-[0.18em] text-sky mb-3">Hover a competitor</div>
              <p className="text-xs text-ftext2 leading-relaxed mb-4">Move over any pill on the grid to see its strengths and the gap versus Streamline360.</p>
              <div className="mt-auto space-y-1.5">
                {c.quadrants.map((q) => {
                  const count = c.players.filter((p) => p.quadrant === q.key && !p.highlight).length;
                  const meta = quadrantMeta[q.key];
                  return (
                    <div key={q.key} className="flex items-center justify-between gap-2 font-mono-data text-[10px] uppercase tracking-wider">
                      <span className="flex items-center gap-1.5">
                        <span className={`w-2 h-2 rounded-full ${meta.dot}`} />
                        <span className={meta.tone}>{q.label}</span>
                      </span>
                      <span className="text-ftext3">{count} competitors</span>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Advantages table moved to the Buyer Dimensions slide */}

      <p className="mt-2 text-[10px] text-ftext3 italic leading-snug max-w-4xl">{c.disclaimer}</p>
    </SectionShell>
  );
}

/* ------------------------------------------------------------------ Verticals (split from Market) */
export function Verticals({ data, common }) {
  const mk = data.market;
  const v = mk.verticals;
  if (!v) return null;
  return (
    <SectionShell id="verticals" eyebrow="Verticals" headline={v.title} {...common}>
      {v.subtitle && (
        <p className="text-base md:text-lg text-ftext2 max-w-3xl leading-relaxed mb-8">{v.subtitle}</p>
      )}
      <div data-testid="market-verticals" className="grid gap-3 md:grid-cols-2 lg:grid-cols-4 auto-rows-fr">
        {v.items.map((item) => {
          const prioTone = item.priority === "Primary beachhead"
            ? "border-[#10B981]/50 bg-[#10B981]/10 text-[#10B981]"
            : item.priority === "Adjacent expansion"
            ? "border-sky/50 bg-sky/10 text-sky"
            : "border-line2 text-ftext2";
          return (
          <div key={item.key} data-testid={`market-vertical-${item.key}`} className="border border-line bg-panel p-5 flex flex-col h-full">
            <div className="flex items-center gap-2 mb-2">
              <Icon name={item.icon} className="w-5 h-5 text-sky" />
              <span className="font-heading font-black text-lg text-ftext leading-tight">{item.label}</span>
            </div>
            {item.priority && (
              <span className={`inline-block self-start font-mono-data text-[9px] font-black uppercase tracking-[0.14em] border px-1.5 py-0.5 mb-2 ${prioTone}`}>
                {item.priority}
              </span>
            )}
            <p className="text-sm text-ftext2 leading-relaxed flex-1">{item.body}</p>
            {/* Partner route block removed per request */}
          </div>
          );
        })}
      </div>
    </SectionShell>
  );
}

/* ------------------------------------------------------------------ Market */
export function Market({ data, common }) {
  const mk = data.market;
  const [mktSourcesOpen, setMktSourcesOpen] = useState(false);
  const somTier = mk.tiers.find((t) => t.key === "som") || {};
  const funnel = [
    { key: "tam", widthClass: "w-full",        tone: "bg-[#10B981]", pct: "100%",         detail: "Screening universe" },
    { key: "sam", widthClass: "w-full md:w-[60%]",    tone: "bg-sky",       pct: "25% of TAM",   detail: "Serviceable today" },
    { key: "som", widthClass: "w-full md:w-[40%]",    tone: "bg-[#8B5CF6]", pct: somTier.share_of_sam || "~3.5% of SAM", detail: "Obtainable in scale-up" },
  ];
  const byKey = Object.fromEntries(mk.tiers.map((t) => [t.key, t]));
  return (
    <SectionShell id="market" eyebrow={mk.eyebrow} headline={mk.headline} {...common}>
      {mk.summary && (
        <p className="text-xs md:text-sm text-ftext2 max-w-3xl leading-relaxed mb-3">{mk.summary}</p>
      )}

      {/* Realistic market layering removed per request */}

      {/* Bottom-up SOM equation */}
      {mk.som_equation && (
        <div data-testid="som-equation" className="border border-line bg-panel p-4 mb-5">
          <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
            <div className="font-mono-data text-xs font-black uppercase tracking-[0.18em] text-sky">{mk.som_equation.title}</div>
            <span className="font-mono-data text-[10px] font-bold uppercase tracking-wider text-ftext3 border border-line2 px-1.5 py-0.5">Illustrative planning scenario</span>
          </div>
          <div className="grid gap-2 md:grid-cols-4 mb-3">
            {mk.som_equation.inputs.map((inp, i) => (
              <div key={i} data-testid={`som-input-${i}`} className="border border-line2 bg-ink p-2">
                <div className="font-mono-data text-[9px] font-bold uppercase tracking-wider text-ftext3">{inp.label}</div>
                <div className="font-heading font-black text-xl text-ftext leading-tight mt-0.5">{inp.value}</div>
              </div>
            ))}
          </div>
          <div className="flex items-center justify-between gap-4 border-t border-line pt-2 flex-wrap">
            <span className="font-mono-data text-xs font-bold uppercase tracking-wider text-ftext2">Formula · {mk.som_equation.formula}</span>
            <span className="font-heading font-black text-xl md:text-2xl text-[#10B981]">{mk.som_equation.result}</span>
          </div>
          {mk.som_equation.note && (
            <p className="text-[11px] text-ftext3 italic mt-2 leading-snug">{mk.som_equation.note}</p>
          )}
        </div>
      )}

      {/* Funnel */}
      <div data-testid="market-funnel" className="space-y-2 mb-3">
        {funnel.map((f) => {
          const t = byKey[f.key];
          if (!t) return null;
          return (
            <div key={f.key} data-testid={`market-${f.key}-bar`} className={`${f.widthClass}`}>
              <div className={`${f.tone} px-4 md:px-5 py-3 md:py-4 flex items-center justify-between gap-4 shadow-sm`}>
                <div className="flex items-center gap-3 md:gap-4 min-w-0">
                  <span className="font-heading font-black text-xl md:text-3xl text-white leading-none">{t.label}</span>
                  <span className="hidden md:inline-block h-6 w-px bg-white/40" />
                  <span className="font-heading font-black text-lg md:text-2xl text-white leading-none tracking-tight whitespace-nowrap">{t.value}</span>
                </div>
                <span className="font-mono-data text-[9px] md:text-[11px] font-black uppercase tracking-[0.16em] text-white/85 whitespace-nowrap">{f.pct}</span>
              </div>
            </div>
          );
        })}
      </div>

      {/* SOM stacked parts — rendered full-width underneath the funnel */}
      {somTier.parts && (
        <div data-testid="market-som-parts" className="mb-3 grid gap-2 md:grid-cols-2">
          {somTier.parts.map((p, i) => (
            <div key={i} className="border border-line2 bg-ink px-3 py-2 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <div className="font-mono-data text-[9px] font-black uppercase tracking-wider text-[#8B5CF6]">{p.label}</div>
                <div className="text-[11px] text-ftext2 leading-snug">{p.detail}</div>
              </div>
              <div className="font-heading font-black text-lg text-ftext whitespace-nowrap">{p.value}</div>
            </div>
          ))}
        </div>
      )}

      {mk.below_bars_note && (
        <p data-testid="market-below-bars-note" className="text-sm text-ftext2 leading-snug mb-3 max-w-4xl">{mk.below_bars_note}</p>
      )}

      {/* Captions */}
      <div className="grid gap-2 md:grid-cols-3 border-t border-line pt-3">
        {mk.tiers.map((t, i) => {
          const dot = ["bg-[#10B981]", "bg-sky", "bg-[#8B5CF6]"][i];
          return (
            <div key={t.key} data-testid={`market-${t.key}-caption`} className="flex items-start gap-2">
              <span className={`mt-1 w-2.5 h-2.5 shrink-0 ${dot}`} />
              <div>
                <div className="font-mono-data font-black text-[10px] uppercase tracking-[0.18em] text-ftext2">{t.label}</div>
                <div className="font-heading font-bold text-base text-ftext leading-snug mb-1">{funnel[i].detail}</div>
                <p className="text-sm text-ftext2 leading-relaxed">{t.desc}</p>
              </div>
            </div>
          );
        })}
      </div>

      {mk.sources && mk.sources.length > 0 && (
        <div data-testid="market-sources" className="mt-4 border border-line/60 bg-panel/30">
          <button
            type="button"
            data-testid="market-sources-toggle"
            onClick={() => setMktSourcesOpen((v) => !v)}
            aria-expanded={mktSourcesOpen}
            className="w-full flex items-center justify-between gap-3 px-3 py-2 hover:bg-panel/70 transition-colors"
          >
            <span className="font-mono-data text-[10px] font-black uppercase tracking-[0.18em] text-ftext3">Sources & citations</span>
            <ChevronRight className={`w-3.5 h-3.5 text-ftext3 transition-transform ${mktSourcesOpen ? "rotate-90" : ""}`} />
          </button>
          {mktSourcesOpen && (
            <ul data-testid="market-sources-content" className="border-t border-line/60 px-3 py-2 space-y-1">
              {mk.sources.map((s, i) => (
                <li key={i} className="text-[11px] text-ftext3 leading-snug flex items-start gap-1.5">
                  <span className="text-sky mt-0.5">·</span>
                  <span>
                    <span className="font-semibold text-ftext2">{s.label}:</span>{" "}
                    {s.url ? (
                      <a href={s.url} target="_blank" rel="noopener noreferrer" className="underline hover:text-sky transition-colors">
                        {s.source}
                      </a>
                    ) : (
                      <span>{s.source}</span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
      {mk.footer_note && (
        <p data-testid="market-footer-note" className="mt-3 text-[11px] italic text-ftext3 leading-snug max-w-4xl">{mk.footer_note}</p>
      )}
    </SectionShell>
  );
}

/* ------------------------------------------------------------------ Financials */
export function Financials({ data, common }) {
  const { isInternal } = useMode();
  const f = data.financials;
  const [expanded, setExpanded] = useState(false);
  const level2 = f.level2.filter((l) => isInternal || !l.internal_only);
  return (
    <SectionShell id="financials" eyebrow={f.eyebrow} headline={f.headline} {...common}>
      {f.subhead && (
        <p data-testid="fin-arr-emphasis" className="text-sm md:text-base text-ftext2 max-w-3xl leading-relaxed mb-4">{f.subhead}</p>
      )}
      {(f.cash_status || f.key_assumptions) && (
        <div data-testid="fin-cash-assumptions" className="grid gap-3 md:grid-cols-2 mb-4">
          {f.cash_status && (
            <div className="border border-line bg-panel p-4 flex flex-wrap gap-x-6 gap-y-2 items-center">
              <div className="font-mono-data text-[10px] font-black uppercase tracking-[0.18em] text-sky w-full">Cash status</div>
              <div><div className="font-mono-data text-[10px] uppercase tracking-wider text-ftext3">Current ARR</div><div className="font-heading font-black text-base text-ftext">{f.cash_status.current_arr}</div></div>
              <div><div className="font-mono-data text-[10px] uppercase tracking-wider text-ftext3">Monthly burn</div><div className="font-heading font-black text-base text-ftext">{f.cash_status.monthly_burn}</div></div>
              <div><div className="font-mono-data text-[10px] uppercase tracking-wider text-ftext3">Runway</div><div className="font-heading font-black text-base text-ftext">{f.cash_status.runway}</div></div>
            </div>
          )}
          {f.key_assumptions && (
            <div className="border border-line bg-panel p-4">
              <div className="font-mono-data text-[10px] font-black uppercase tracking-[0.18em] text-[#10B981] mb-2">Key assumptions</div>
              <div className="grid grid-cols-1 gap-y-1.5 text-sm">
                {f.key_assumptions.map((a, i) => (
                  <div key={i} className="flex items-start justify-between gap-3">
                    <span className="text-ftext2">{a.label}</span>
                    <span className="font-mono-data font-bold text-ftext text-right">{a.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      <div className="grid gap-3 lg:grid-cols-5">
        <div className="lg:col-span-3 border border-line bg-panel overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-line font-mono-data text-xs uppercase tracking-wider text-ftext3">
                <th className="text-left px-4 py-3">Metric</th>
                {["2026", "2027", "2028", "2029", "2030"].map((y) => (
                  <th key={y} className="text-right px-4 py-3">{y}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {f.summary.map((row) => (
                <tr key={row.metric} data-testid={`fin-row-${row.metric.toLowerCase().replace(/\s+/g, "-")}`} className="border-b border-line/60 last:border-0">
                  <td className="px-4 py-3 text-ftext">{row.metric}</td>
                  {[row.y1, row.y2, row.y3, row.y4, row.y5].map((v, i) => {
                    const s = String(v);
                    const neg = s.startsWith("-");
                    return (
                      <td key={i} className={`px-4 py-3 text-right font-mono-data whitespace-nowrap ${neg ? "text-[#F87171]" : "text-ftext"}`}>
                        {neg ? `\u2013${f.currency}${s.slice(1)}` : `${f.currency}${s}`}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="lg:col-span-2 border border-line bg-panel p-5 flex flex-col justify-between">
          <div className="font-mono-data text-xs uppercase tracking-wider text-ftext3 mb-2">Break-even</div>
          <div className="font-heading font-black text-3xl text-ftext leading-tight">{f.break_even.year}</div>
          <p className="text-sm text-ftext2 mt-2 leading-relaxed">{f.break_even.text}</p>
        </div>
      </div>

      {/* Prominent revenue / spend / EBITDA chart */}
      <div className="mt-3 border border-line bg-panel p-4 md:p-5">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="font-mono-data text-xs uppercase tracking-wider text-ftext3">
            Revenue · operating spend · EBITDA ({f.currency}M)
          </div>
          <div className="flex items-center gap-4 font-mono-data text-xs uppercase tracking-wider">
            <span className="flex items-center gap-1.5 text-sky"><span className="w-2.5 h-2.5 bg-sky" /> Revenue</span>
            <span className="flex items-center gap-1.5 text-[#F59E0B]"><span className="w-4 h-0.5 bg-[#F59E0B]" /> Spend</span>
            <span className="flex items-center gap-1.5 text-[#F87171]"><span className="w-4 h-0.5 bg-[#F87171]" /> EBITDA</span>
          </div>
        </div>
        <ResponsiveContainer width="100%" height={260}>
          <ComposedChart data={f.ramp} margin={{ top: 24, right: 12, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="revFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#38BDF8" stopOpacity={0.9} />
                <stop offset="100%" stopColor="#38BDF8" stopOpacity={0.3} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#1E293B" vertical={false} />
            <XAxis dataKey="year" stroke="#64748B" fontSize={12} tickLine={false} axisLine={{ stroke: "#1E293B" }} />
            <YAxis stroke="#64748B" fontSize={12} tickLine={false} axisLine={false} />
            <Tooltip cursor={{ fill: "rgba(56,189,248,0.08)" }} {...chartTooltip} />
            <ReferenceLine y={0} stroke="#475569" strokeDasharray="3 3" />
            <Bar dataKey="revenue" name="Revenue" fill="url(#revFill)" maxBarSize={54}>
              <LabelList dataKey="revenue" position="top" formatter={(v) => `${f.currency}${v}M`} fill="#F1F5F9" fontSize={11} fontFamily="JetBrains Mono, monospace" />
            </Bar>
            <Line dataKey="spend" name="Spend" stroke="#F59E0B" strokeWidth={2.5} dot={{ r: 3, fill: "#F59E0B" }} />
            <Line dataKey="ebitda" name="EBITDA" stroke="#F87171" strokeWidth={2.5} dot={{ r: 4, fill: "#F87171" }} activeDot={{ r: 6 }}>
              <LabelList dataKey="ebitda" position="bottom" formatter={(v) => `${f.currency}${v}M`} fill="#94A3B8" fontSize={10} fontFamily="JetBrains Mono, monospace" />
            </Line>
          </ComposedChart>
        </ResponsiveContainer>
        <p className="mt-3 font-mono-data text-xs text-ftext3 leading-relaxed">
          EBIT turns positive in 2030 (€13.0M) after a €10M Series A in 2028 funds the push to break-even.
        </p>
      </div>

      {/* Reconciliation + driver bridge removed per request */}

      {/* Channel mix moved to next slide */}

      {f.footnote && (
        <p data-testid="fin-footnote" className="mt-4 text-xs text-ftext3 italic leading-relaxed">{f.footnote}</p>
      )}

      <button
        data-testid="fin-expand-btn"
        onClick={() => setExpanded((e) => !e)}
        className="mt-8 flex items-center gap-2 font-mono-data text-sm text-sky hover:text-skyH transition-colors"
      >
        <ChevronRight className={`w-4 h-4 transition-transform ${expanded ? "rotate-90" : ""}`} />
        {expanded ? "Hide" : "Show"} Level 2 — assumptions
      </button>

      {expanded && (
        <div data-testid="fin-level2" className="mt-4 grid gap-px bg-line border border-line fade-up">
          {level2.map((l, i) => (
            <div key={i} className="bg-panel p-5">
              <div className="mb-2">
                <span className="font-heading font-bold text-ftext">{l.assumption}</span>
              </div>
              <p className="text-sm text-ftext2 leading-relaxed">{l.detail}</p>
              {l.warning && <ReviewWarning className="mt-3">{l.warning}</ReviewWarning>}
            </div>
          ))}
        </div>
      )}
    </SectionShell>
  );
}

/* ------------------------------------------------------------------ ChannelMix (split from Financials) */
export function ChannelMix({ data, common }) {
  const f = data.financials;
  const cm = f.channel_mix;
  if (!cm) return null;
  return (
    <SectionShell id="channelmix" eyebrow="Channel Mix" headline={cm.title} {...common}>
      {cm.subtitle && (
        <p className="text-base md:text-lg text-ftext2 max-w-3xl leading-relaxed mb-6">{cm.subtitle}</p>
      )}
      <div data-testid="fin-channel-mix" className="border border-line bg-panel p-5">
        <div className="overflow-x-auto">
          <table className="w-full text-sm border border-line">
            <thead>
              <tr className="border-b border-line bg-ink font-mono-data text-[10px] uppercase tracking-wider text-ftext3">
                <th className="text-left px-3 py-2">Year</th>
                <th className="text-right px-3 py-2">Total net ARR</th>
                <th className="text-right px-3 py-2">Partner / OEM</th>
                <th className="text-right px-3 py-2">Direct end-user</th>
                <th className="text-left px-3 py-2 min-w-[200px]">Mix (partner ↔ end-user)</th>
              </tr>
            </thead>
            <tbody>
              {cm.rows.map((row) => (
                <tr key={row.year} data-testid={`fin-mix-${row.year}`} className="border-b border-line/60 last:border-0">
                  <td className="px-3 py-2.5 font-mono-data text-ftext">{row.year}</td>
                  <td className="px-3 py-2.5 text-right font-mono-data font-bold text-ftext">{row.arr}</td>
                  <td className="px-3 py-2.5 text-right font-mono-data text-sky">{row.partner_arr} <span className="text-ftext3">({row.partner_pct}%)</span></td>
                  <td className="px-3 py-2.5 text-right font-mono-data text-[#10B981]">{row.enduser_arr} <span className="text-ftext3">({row.enduser_pct}%)</span></td>
                  <td className="px-3 py-2.5">
                    <div className="flex h-2.5 w-full overflow-hidden bg-ink border border-line2" role="img" aria-label={`${row.partner_pct}% partner, ${row.enduser_pct}% end-user`}>
                      <div className="h-full bg-sky" style={{ width: `${row.partner_pct}%` }} />
                      <div className="h-full bg-[#10B981]" style={{ width: `${row.enduser_pct}%` }} />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {cm.note && (
          <p className="text-xs text-ftext3 italic mt-3 leading-relaxed">{cm.note}</p>
        )}
      </div>
    </SectionShell>
  );
}

/* ------------------------------------------------------------------ Raise */
export function Raise({ data, common }) {
  const r = data.raise;
  return (
    <SectionShell id="raise" eyebrow={r.eyebrow} headline={r.headline} {...common}>
      {r.subhead && (
        <p className="text-sm md:text-base text-ftext2 max-w-3xl leading-relaxed mb-5">{r.subhead}</p>
      )}
      <div className="grid gap-px bg-line border border-line sm:grid-cols-2 lg:grid-cols-4 mb-6">
        {r.terms.map((t) => (
          <div key={t.label} data-testid={`raise-term-${t.label.toLowerCase().replace(/\s+/g, "-")}`} className="bg-panel p-4">
            <div className="font-mono-data text-xs uppercase tracking-wider text-ftext3 mb-1.5">{t.label}</div>
            <div className="font-heading font-black text-xl text-ftext">{t.value}</div>
          </div>
        ))}
      </div>

      <div>
        <div className="font-mono-data text-xs uppercase tracking-wider text-ftext3 mb-3">Use of funds</div>
        <div className="grid gap-4 md:grid-cols-3">
          {r.use_of_funds.map((u) => (
            <div key={u.key} data-testid={`use-${u.key}`} className="border border-line bg-panel p-4">
              <div className="flex items-center justify-between mb-2 gap-3">
                <span className="text-sm text-ftext font-bold">{u.label}</span>
                <span className="font-mono-data text-sm text-sky whitespace-nowrap">{u.pct}%{u.amount ? ` · ${u.amount}` : ""}</span>
              </div>
              <div className="h-1.5 w-full bg-line mb-2">
                <div className="h-1.5 bg-sky" style={{ width: `${u.pct}%` }} />
              </div>
              <p className="text-xs text-ftext2 leading-snug">{u.detail}</p>
            </div>
          ))}
        </div>
      </div>

      {r.milestones_24m && (
        <div data-testid="raise-milestones" className="mt-6">
          <div className="font-mono-data text-xs uppercase tracking-wider text-ftext3 mb-3">{r.milestones_24m.title}</div>
          <div className="grid gap-px bg-line border border-line sm:grid-cols-2 lg:grid-cols-4">
            {r.milestones_24m.items.map((m, i) => (
              <div key={i} className="bg-panel p-4">
                <div className="font-mono-data text-[10px] font-black uppercase tracking-[0.18em] text-[#10B981] mb-1.5">{m.label}</div>
                <div className="font-heading font-black text-base text-ftext leading-tight">{m.value}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </SectionShell>
  );
}

/* ------------------------------------------------------------------ Future Scenario (appendix) */
export function FutureScenario({ data, common }) {
  const fs = data.future_scenario;
  if (!fs) return null;
  return (
    <SectionShell id="scenario" eyebrow={fs.eyebrow} headline={fs.headline} {...common}>
      {fs.subhead && (
        <p className="text-base md:text-lg text-ftext2 max-w-4xl leading-relaxed mb-6">{fs.subhead}</p>
      )}
      <div data-testid="raise-future-scenario" className="grid gap-6 md:grid-cols-2">
        <div className="border border-line bg-panel p-5">
          <div className="flex items-center justify-between gap-3 mb-4">
            <div className="font-mono-data text-xs font-black uppercase tracking-[0.18em] text-sky">Conservative case</div>
            <span className="font-mono-data text-[10px] font-bold uppercase tracking-wider text-ftext3 border border-line2 px-1.5 py-0.5">Illustrative · appendix</span>
          </div>
          <div className="space-y-2">
            {fs.rows.map((row, i) => (
              <div key={i} className="flex items-center justify-between gap-3 text-sm border-b border-line last:border-b-0 py-2">
                <span className="text-ftext2">{row.label}</span>
                <span className="font-mono-data text-base font-bold text-ftext">{row.value}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="border border-line bg-panel p-5">
          <div className="font-mono-data text-xs font-black uppercase tracking-[0.18em] text-sky mb-4">{fs.sensitivity_title || "Sensitivity range"}</div>
          <div className="space-y-3">
            {(fs.sensitivity_rows || []).map((s, i) => (
              <div key={i} data-testid={`raise-sensitivity-${i}`} className="border border-line2 bg-ink p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="font-mono-data text-sm font-bold text-ftext">{s.label}</span>
                  <span className="font-mono-data text-sm text-ftext2">{s.ev}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-xs text-ftext3">20% stake</span>
                  <span className="font-mono-data text-sm font-black text-[#10B981]">{s.stake}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {fs.sources && (
        <p className="text-[11px] text-ftext3 mt-2 leading-relaxed max-w-3xl">{fs.sources}</p>
      )}
    </SectionShell>
  );
}

/* ------------------------------------------------------------------ Team card renderer (shared) */
function TeamCard({ m }) {
  const [open, setOpen] = useState(false);
  const hasDetails = Boolean(m.bio || m.highlight || m.ft_status || m.responsibility || m.ownership_note);
  return (
    <div key={m.key} data-testid={`team-${m.key}`} className="border border-line bg-panel p-4 flex flex-col h-full">
      {m.photo ? (
        <div className={`aspect-[4/3] w-full border border-line2 mb-3 overflow-hidden ${m.photo_bg || "bg-ink"}`}>
          <img
            src={m.photo}
            alt={m.name}
            className={`w-full h-full ${m.photo_fit || "object-cover"} ${m.photo_position || "object-center"} ${m.photo_scale || ""}`}
          />
        </div>
      ) : (
        <div className="flex aspect-[4/3] w-full items-center justify-center border border-line2 bg-ink font-mono-data text-2xl text-sky mb-3">
          {m.name === "Profile pending" ? "?" : m.name.split(" ").map((n) => n[0]).join("").slice(0, 2)}
        </div>
      )}
      <div className="font-mono-data text-[11px] uppercase tracking-wider text-sky mb-0.5">{m.role}</div>
      <div className="font-heading font-bold text-base text-ftext mb-1.5 leading-tight">{m.name}</div>
      {m.registered_name && (
        <div data-testid={`team-${m.key}-registered`} className="font-mono-data text-[10px] text-ftext3 leading-tight mb-1.5">Registered: {m.registered_name}</div>
      )}

      {hasDetails && (
        <>
          <button
            type="button"
            data-testid={`team-${m.key}-toggle`}
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            className="mt-2 w-full flex items-center justify-between gap-2 border-t border-line pt-2 font-mono-data text-[10px] font-black uppercase tracking-[0.18em] text-ftext2 hover:text-sky transition-colors"
          >
            <span>{open ? "Hide details" : "Show details"}</span>
            <ChevronRight className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-90" : ""}`} />
          </button>
          {open && (
            <div data-testid={`team-${m.key}-details`} className="mt-2 space-y-2">
              {m.bio && <p className="text-xs text-ftext2 leading-snug">{m.bio}</p>}
              {m.highlight && (
                <p data-testid={`team-${m.key}-highlight`} className="border-l-2 border-sky pl-2.5 text-xs text-ftext leading-snug">
                  {m.highlight}
                </p>
              )}
              {(m.ft_status || m.responsibility || m.ownership_note) && (
                <div data-testid={`team-${m.key}-execsummary`} className="border border-line2 bg-ink p-2 space-y-0.5">
                  {m.ft_status && (
                    <div className="flex items-start gap-1.5">
                      <span className="font-mono-data text-[9px] font-black uppercase tracking-wider text-ftext3 shrink-0">Status</span>
                      <span className="text-[11px] text-ftext leading-snug">{m.ft_status}</span>
                    </div>
                  )}
                  {m.responsibility && (
                    <div className="flex items-start gap-1.5">
                      <span className="font-mono-data text-[9px] font-black uppercase tracking-wider text-ftext3 shrink-0">Role</span>
                      <span className="text-[11px] text-ftext2 leading-snug">{m.responsibility}</span>
                    </div>
                  )}
                  {m.ownership_note && (
                    <div className="flex items-start gap-1.5">
                      <span className="font-mono-data text-[9px] font-black uppercase tracking-wider text-ftext3 shrink-0">Equity</span>
                      <span className="text-[11px] text-ftext3 italic leading-snug">{m.ownership_note}</span>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {m.linkedin && (
        <a
          href={m.linkedin}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`team-${m.key}-linkedin`}
          className="mt-auto pt-2.5 inline-flex items-center gap-1 font-mono-data text-[11px] uppercase tracking-wider text-sky hover:underline self-start"
        >
          <Linkedin className="h-3 w-3" /> LinkedIn
        </a>
      )}
    </div>
  );
}

function teamRenderCard(m) {
  return <TeamCard key={m.key} m={m} />;
}

/* ------------------------------------------------------------------ Leadership */
export function Leadership({ data, common }) {
  const t = data.team;
  const leadership = t.members.filter((m) => m.group === "leadership");
  const members = leadership.length > 0 ? leadership : t.members;
  return (
    <SectionShell id="leadership" eyebrow="Leadership" headline="Executive management" {...common}>
      <div data-testid="team-moat-statement" className="mb-6 border-l-4 border-sky bg-panel px-5 py-3 max-w-4xl">
        <div className="font-mono-data font-bold text-xs uppercase tracking-[0.2em] text-sky mb-1">Decades inside the plants we serve.</div>
        <p className="font-heading font-bold text-base md:text-xl text-ftext leading-snug">{t.disclaimer}</p>
      </div>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {members.map(teamRenderCard)}
      </div>
    </SectionShell>
  );
}

/* ------------------------------------------------------------------ Founding team */
export function Founders({ data, common }) {
  const t = data.team;
  const cofounders = t.members.filter((m) => m.group === "cofounders");
  const members = cofounders.length > 0 ? cofounders : [];
  if (members.length === 0) return null;
  return (
    <SectionShell id="founders" eyebrow="Founding team" headline="The founders" {...common}>
      <p className="text-sm md:text-base text-ftext2 mb-6 max-w-3xl">The operators behind the product, the plant relationships and the go-to-market channels — deep industrial, pharma and automation experience across Europe and India.</p>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {members.map(teamRenderCard)}
      </div>
      {false && t.diligence_note && (
        <p data-testid="founders-diligence-note" className="mt-5 text-xs text-ftext3 italic leading-relaxed max-w-4xl border-l-2 border-line2 pl-3">
          <span className="font-mono-data text-[10px] font-black uppercase tracking-[0.14em] text-ftext2 mr-2">Diligence</span>
          {t.diligence_note}
        </p>
      )}
    </SectionShell>
  );
}

/* ------------------------------------------------------------------ Business Model */
export function BusinessModel({ data, common }) {
  const bm = data.business_model;
  const [opening, setOpening] = useState(false);
  if (!bm?.url || !data.investor?.business_model_access) return null;
  const host = (() => { try { return new URL(bm.url).host; } catch { return bm.url; } })();
  const isPreview = !!data.investor?.preview;
  const openSimulator = async () => {
    trackCtaClick("open_business_model", { from: "business_model_slide" });
    if (isPreview) {
      // Founder preview — no SSO token available; just open the raw URL
      window.open(bm.url, "_blank", "noopener,noreferrer");
      return;
    }
    // Open the tab synchronously so popup blockers don't fire. Note: we omit
    // `noopener` here because it causes `window.open` to return `null` and we
    // need the handle to navigate the new tab once the SSO token is ready.
    const w = window.open("about:blank", "_blank");
    try {
      setOpening(true);
      const { data: res } = await api.post("/sso/simulator-token");
      if (w && !w.closed) {
        try { w.opener = null; } catch (_) { /* best-effort */ }
        w.location.href = res.redirect_url;
      } else {
        // Popup was blocked — navigate current tab as fallback
        window.location.href = res.redirect_url;
      }
    } catch (e) {
      if (w && !w.closed) w.close();
      const msg = e?.response?.data?.detail || "Could not sign you in to the simulator. Please try again.";
      toast.error(msg);
    } finally {
      setOpening(false);
    }
  };
  return (
    <SectionShell id="business_model" eyebrow={bm.eyebrow} headline={bm.headline} {...common}>
      <p className="text-base text-ftext2 max-w-3xl leading-relaxed mb-6">{bm.intro}</p>
      <div className="border border-line bg-panel p-6 md:p-8 max-w-3xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="flex h-11 w-11 items-center justify-center border border-[#10B981]/40 bg-[#10B981]/10">
            <FileSpreadsheet className="w-5 h-5 text-[#10B981]" />
          </div>
          <div>
            <div className="font-mono-data text-[10px] font-black uppercase tracking-[0.18em] text-[#10B981]">Shared with you</div>
            <div className="font-heading font-black text-lg text-ftext leading-tight">{host}</div>
          </div>
        </div>
        <button
          type="button"
          data-testid="business-model-open-btn"
          onClick={openSimulator}
          disabled={opening}
          className="inline-flex items-center gap-2 bg-sky text-primary-foreground font-mono-data text-sm font-black uppercase tracking-wider px-5 py-3 hover:brightness-110 transition-all disabled:opacity-60 disabled:cursor-wait"
        >
          {opening ? "Signing you in…" : (bm.cta_label || "Open business model")}
          <ArrowUpRight className="w-4 h-4" />
        </button>
        <p className="mt-4 text-[11px] text-ftext3 italic leading-snug">
          Signs you in automatically with your investor email — no second password needed. Please keep the contents confidential.
        </p>
      </div>
    </SectionShell>
  );
}

/* ------------------------------------------------------------------ Sandbox */
export function Sandbox({ data, common }) {
  const sb = data.sandbox;
  if (!sb.url || !data.investor?.sandbox_access) return null;
  return (
    <SectionShell id="sandbox" eyebrow={sb.eyebrow} headline={sb.headline} {...common}>
      <p className="text-base text-ftext2 max-w-2xl leading-relaxed mb-6">{sb.intro}</p>
      <div className="border border-line bg-panel">
        <div className="flex items-center justify-between border-b border-line px-4 py-2.5">
          <div className="flex items-center gap-2 font-mono-data text-xs text-ftext3">
            <span className="flex gap-1.5">
              <span className="w-2.5 h-2.5 rounded-full bg-line2" />
              <span className="w-2.5 h-2.5 rounded-full bg-line2" />
              <span className="w-2.5 h-2.5 rounded-full bg-sky" />
            </span>
            <span className="ml-2 truncate max-w-[16rem] sm:max-w-md">{sb.url}</span>
          </div>
          <a
            data-testid="sandbox-open-btn"
            href={sb.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => trackCtaClick("open_sandbox", { from: "sandbox_terminal" })}
            className="flex items-center gap-1.5 font-mono-data text-xs uppercase tracking-wider text-sky hover:text-skyH transition-colors"
          >
            Open in new tab <ExternalLink className="w-3.5 h-3.5" />
          </a>
        </div>
        <iframe
          data-testid="sandbox-iframe"
          src={sb.url}
          title="Streamline360 Product Sandbox"
          className="w-full h-[70vh] bg-ink"
        />
      </div>
      {sb.demo_video_url ? (
        <div className="mt-4 border border-line bg-panel">
          <iframe data-testid="demo-video" src={sb.demo_video_url} title="Demo walkthrough" className="w-full h-[60vh]" allowFullScreen />
        </div>
      ) : null}
    </SectionShell>
  );
}

/* ------------------------------------------------------------------ Final CTA */
export function FinalCTA({ data, common }) {
  const c = data.cta;
  const sandboxUrl = data.sandbox?.url;
  const hasSandbox = !!(data.investor?.sandbox_access && sandboxUrl);

  const handle = (action) => {
    trackCtaClick(action.key, { label: action.label, sandbox_granted: hasSandbox });
    const email = c.contact_email;
    if (action.key === "request_sandbox") {
      if (hasSandbox) window.open(sandboxUrl, "_blank", "noopener");
      else window.location.href = `mailto:${email}?subject=Request Streamline360 sandbox access`;
    } else if (action.key === "request_model") {
      window.location.href = `mailto:${email}?subject=Request Streamline360 model / data room`;
    } else if (action.key === "talk_to_team") {
      if (c.calendly_url) window.open(c.calendly_url, "_blank", "noopener");
      else window.location.href = `mailto:${email}?subject=Streamline360 — schedule a call with the team`;
    }
  };

  const actions = c.actions.map((a) =>
    a.key === "request_sandbox" && hasSandbox ? { ...a, label: a.granted_label, desc: a.granted_desc } : a
  );

  return (
    <SectionShell id="cta" {...common}>
      <div className="relative grid-motif -mx-6 md:-mx-14 lg:-mx-20 px-6 md:px-14 lg:px-20 py-6">
        <div className="absolute top-0 left-1/3 w-96 h-96 bg-sky opacity-[0.06] blur-3xl rounded-full pointer-events-none" />
        <h2 className="font-heading font-black tracking-tight text-3xl md:text-5xl text-ftext mb-3">{c.headline}</h2>
        <p className="text-base text-ftext2 max-w-xl mb-6 leading-relaxed">{c.subhead}</p>
        {c.check_this_week && (
          <div data-testid="cta-check-this-week" className="mb-8 border border-sky/40 bg-sky/5 p-5 md:p-6">
            <div className="font-mono-data text-[11px] font-black uppercase tracking-[0.22em] text-sky mb-4">{c.check_this_week.title}</div>
            <div className="grid gap-4 md:grid-cols-3">
              {c.check_this_week.items.map((it, i) => (
                <div key={i} className="flex items-start gap-3">
                  <div className="flex h-9 w-9 items-center justify-center border border-[#10B981]/40 bg-[#10B981]/10 shrink-0">
                    <Icon name={it.icon} className="w-4 h-4 text-[#10B981]" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-heading font-black text-base text-ftext leading-tight">{it.title}</div>
                    <p className="text-sm text-ftext2 leading-snug mt-1">{it.body}</p>
                  </div>
                </div>
              ))}
            </div>
            {c.check_this_week.footer && (
              <div className="mt-4 pt-4 border-t border-sky/20 font-mono-data text-xs uppercase tracking-wider text-ftext2">{c.check_this_week.footer}</div>
            )}
          </div>
        )}
        {c.core_message && (
          <div data-testid="cta-core-value" className="mb-8 border-l-4 border-[#10B981] bg-panel/60 px-4 py-3 max-w-4xl">
            <div className="font-mono-data text-[10px] font-black uppercase tracking-[0.18em] text-[#10B981] mb-1">Value for investors</div>
            <p className="text-sm md:text-base text-ftext leading-snug">{c.core_message}</p>
          </div>
        )}
        <div className="grid gap-4 sm:grid-cols-3">
          {actions.map((a) => (
            <button
              key={a.key}
              data-testid={`cta-${a.key}`}
              onClick={() => handle(a)}
              className="group flex flex-col justify-between gap-4 border border-line bg-panel p-6 text-left transition-colors hover:border-sky min-h-[220px]"
            >
              <div className="flex items-start gap-4">
                <Icon name={a.icon} className="w-6 h-6 text-sky shrink-0 mt-1" />
                <div className="flex-1 min-w-0">
                  <div className="font-mono-data text-[10px] font-black uppercase tracking-[0.22em] text-sky mb-1">{a.label}</div>
                  <div className="font-heading font-black text-lg text-ftext leading-tight">{a.sub || a.label}</div>
                  <div className="text-sm text-ftext2 mt-1.5 leading-relaxed">{a.desc}</div>
                  {a.deliverables && (
                    <ul data-testid={`cta-${a.key}-deliverables`} className="mt-2.5 space-y-0.5">
                      {a.deliverables.map((d, i) => (
                        <li key={i} className="flex items-start gap-1.5 text-[11px] text-ftext2 leading-snug">
                          <ChevronRight className="w-3 h-3 text-sky mt-0.5 shrink-0" />
                          <span>{d}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {a.route_note && (
                    <div className="text-xs text-ftext3 italic mt-2 leading-relaxed">{a.route_note}</div>
                  )}
                </div>
              </div>
              <div className="flex items-center justify-end">
                <ArrowUpRight className="w-5 h-5 text-ftext3 transition-all group-hover:text-sky group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </div>
            </button>
          ))}
        </div>
        <div className="mt-10 flex items-center gap-2 font-mono-data text-xs uppercase tracking-wider text-ftext3 border-t border-line pt-6">
          <Mail className="w-3.5 h-3.5" />
          Direct: <a href={`mailto:${c.contact_email}`} className="text-sky hover:underline">{c.contact_email}</a>
        </div>
      </div>
    </SectionShell>
  );
}

/* ------------------------------------------------------------------ Go-to-Market */
export function GTM({ data, common }) {
  const g = data.gtm;
  const [showPricing, setShowPricing] = useState(false);
  const groupTone = {
    signed: { border: "border-[#10B981]", label: "bg-[#10B981]/10 text-[#10B981] border-[#10B981]/50", dot: "bg-[#10B981]" },
    advanced: { border: "border-sky", label: "bg-sky/10 text-sky border-sky/50", dot: "bg-sky" },
    initial: { border: "border-line2", label: "bg-panel text-ftext2 border-line2", dot: "bg-ftext3" },
  };
  return (
    <SectionShell id="gtm" eyebrow={g.eyebrow} headline={g.headline} {...common}>
      <p className="text-base md:text-lg font-bold text-ftext max-w-3xl leading-snug mb-4">{g.subhead}</p>

      {/* 5-stage commercial funnel removed per request */}

      {/* Flow diagram: Streamline -> (Partners + Machine builders) -> End users, with pull-effect direct arrow */}
      <div data-testid="gtm-flow-diagram" className="mb-2 border border-line bg-panel p-4 overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-[minmax(180px,1fr)_auto_minmax(200px,1fr)_auto_minmax(180px,1fr)] gap-4 md:gap-3 items-center">
          {/* Streamline node */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5 }}
            className="relative border-2 border-sky bg-sky/10 p-4 md:p-5 text-center"
          >
            <motion.span
              aria-hidden
              className="absolute inset-0 border-2 border-sky pointer-events-none"
              animate={{ opacity: [0.15, 0.6, 0.15] }}
              transition={{ duration: 2.2, repeat: Infinity }}
            />
            <div className="relative font-mono-data text-[10px] font-black uppercase tracking-[0.18em] text-sky mb-1">Streamline360</div>
            <div className="relative font-heading font-black text-base md:text-lg text-ftext leading-tight">Industrial AI platform</div>
          </motion.div>

          {/* arrow 1 with moving dot */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.4, delay: 0.25 }}
            className="relative flex items-center justify-center rotate-90 md:rotate-0 py-2 md:py-0"
          >
            <div className="relative h-[2px] w-full md:w-16 bg-sky/40 overflow-hidden">
              <motion.span
                aria-hidden
                className="absolute top-1/2 -translate-y-1/2 w-2 h-2 bg-sky rounded-full"
                animate={{ left: ["-8px", "calc(100% + 8px)"] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
              />
            </div>
            <ArrowRight className="w-6 h-6 text-sky -ml-1" />
          </motion.div>

          {/* Channels: Partners + Machine builders stacked */}
          <div className="grid grid-cols-1 gap-3">
            {[
              { key: "partners", head: "Channel 1", title: "Partners", sub: "VARs, SIs, analytics partners", border: "border-sky", chip: "text-sky bg-sky/10 border-sky/50" },
              { key: "machine", head: "Channel 2", title: "Machine builders / OEMs", sub: "Embedded at point of sale", border: "border-[#8B5CF6]", chip: "text-[#8B5CF6] bg-[#8B5CF6]/10 border-[#8B5CF6]/50" },
            ].map((c, i) => (
              <motion.div
                key={c.key}
                data-testid={`gtm-node-${c.key}`}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.45, delay: 0.4 + i * 0.15 }}
                className={`border-l-4 ${c.border} border border-line2 bg-ink p-3 md:p-4`}
              >
                <span className={`inline-block font-mono-data text-[10px] font-black uppercase tracking-[0.16em] px-1.5 py-0.5 border ${c.chip} mb-1`}>{c.head}</span>
                <div className="font-heading font-bold text-base text-ftext leading-tight">{c.title}</div>
                <div className="text-[11px] text-ftext2 mt-0.5">{c.sub}</div>
              </motion.div>
            ))}
          </div>

          {/* arrow 2 with moving dot */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.4, delay: 0.7 }}
            className="relative flex items-center justify-center rotate-90 md:rotate-0 py-2 md:py-0"
          >
            <div className="relative h-[2px] w-full md:w-16 bg-sky/40 overflow-hidden">
              <motion.span
                aria-hidden
                className="absolute top-1/2 -translate-y-1/2 w-2 h-2 bg-sky rounded-full"
                animate={{ left: ["-8px", "calc(100% + 8px)"] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
              />
            </div>
            <ArrowRight className="w-6 h-6 text-sky -ml-1" />
          </motion.div>

          {/* End users */}
          <motion.div
            data-testid="gtm-node-endusers"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5, delay: 0.9 }}
            className="border border-line2 bg-panel p-4 md:p-5 text-center"
          >
            <div className="font-mono-data text-[10px] font-black uppercase tracking-[0.18em] text-ftext3 mb-1">End users</div>
            <div className="font-heading font-bold text-base md:text-lg text-ftext leading-tight">Plants at scale</div>
            <div className="text-[11px] text-ftext2 mt-1">Pharma · Automotive · F&B · Materials</div>
          </motion.div>
        </div>

        {/* Direct pull-effect arrow — originates from Streamline node, bypasses channels, lands at End users */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5, delay: 1.1 }}
          className="mt-5 pt-5 border-t border-dashed border-[#10B981]/50 grid grid-cols-1 md:grid-cols-[minmax(180px,1fr)_auto_minmax(200px,1fr)_auto_minmax(180px,1fr)] items-center gap-3"
        >
          {/* under Streamline column: label */}
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 bg-[#10B981]" />
            <span className="font-mono-data text-[10px] font-black uppercase tracking-[0.18em] text-[#10B981] whitespace-nowrap">Direct path</span>
          </div>
          {/* spans across channels: dashed line with moving dot */}
          <div className="hidden md:block col-span-3 relative h-[2px] overflow-visible">
            <div className="absolute inset-0" style={{ backgroundImage: "linear-gradient(to right, rgba(16,185,129,0.7) 60%, transparent 60%)", backgroundSize: "10px 2px" }} />
            <motion.span
              aria-hidden
              className="absolute top-1/2 -translate-y-1/2 w-2 h-2 bg-[#10B981] rounded-full"
              animate={{ left: ["-8px", "calc(100% + 8px)"] }}
              transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
            />
            <ArrowRight className="absolute -right-1 top-1/2 -translate-y-1/2 w-5 h-5 text-[#10B981]" />
          </div>
        </motion.div>
        <div className="mt-3 md:pl-[calc(minmax(180px,1fr)+12px)] text-center md:text-left">
          <span className="inline-block font-mono-data text-[11px] font-black uppercase tracking-[0.16em] text-[#10B981]">
            Streamline360 also sells direct to strategic end users → proves the tech → creates channel pull
          </span>
        </div>
      </div>
      {/* Channels & pricing moved to next slide */}
    </SectionShell>
  );
}

/* ------------------------------------------------------------------ Channels (split from GTM) */
export function Channels({ data, common }) {
  const g = data.gtm;
  const [showPricing, setShowPricing] = useState(false);
  const [showEconomics, setShowEconomics] = useState(false);
  return (
    <SectionShell id="channels" eyebrow="Channels" headline="Three channels, one motion" {...common}>
      <p className="text-base md:text-lg text-ftext2 max-w-3xl leading-relaxed mb-6">Partners and machine builders carry us into plants at scale. Proof on the plant floor then pulls every channel forward.</p>

      {/* Sequenced channels: partners -> machine builders -> end-user pull */}
      <div className="grid gap-4 md:grid-cols-3 mb-4">
        {g.segments.map((s) => (
          <div key={s.title} data-testid={`gtm-seg-${s.title.split(" ")[0].toLowerCase()}`} className="relative border border-line bg-panel p-6">
            <span className="absolute top-5 right-5 font-heading font-black text-3xl text-line select-none">{s.step}</span>
            <div className="flex h-11 w-11 items-center justify-center border border-line2 bg-ink mb-4">
              <Icon name={s.icon} className="w-5 h-5 text-sky" />
            </div>
            <h3 className="font-heading font-black text-lg text-ftext mb-2">{s.title}</h3>
            <p className="text-sm text-ftext2 leading-relaxed">{s.body}</p>
          </div>
        ))}
      </div>
      <div className="mb-6 flex flex-wrap items-center gap-2 font-mono-data text-xs font-bold uppercase tracking-wider text-ftext3">
        Partners <ArrowRight className="w-3.5 h-3.5 text-sky" /> Machine builders <ArrowRight className="w-3.5 h-3.5 text-sky" /> End-user proof <ArrowRight className="w-3.5 h-3.5 text-[#10B981]" /> <span className="text-[#10B981]">pull effect</span>
      </div>

      {/* Pricing — collapsed by default */}
      <div className="mb-4">
        <button
          data-testid="gtm-toggle-pricing"
          type="button"
          onClick={() => setShowPricing((v) => !v)}
          className="w-full flex items-center justify-between border border-line bg-panel px-5 py-3 hover:border-sky transition-colors"
        >
          <span className="font-mono-data text-xs uppercase tracking-wider text-ftext2">
            {showPricing ? "Hide pricing" : "Show pricing"} · annual SaaS, tiered by usage
          </span>
          <ChevronRight className={`w-4 h-4 text-sky transition-transform ${showPricing ? "rotate-90" : ""}`} />
        </button>
        {showPricing && (
          <div data-testid="gtm-pricing-body" className="mt-4">
            <div className="grid gap-4 md:grid-cols-3">
              {g.pricing.map((p) => (
                <div key={p.tier} data-testid={`price-${p.tier.toLowerCase()}`} className={`border bg-panel p-6 ${p.featured ? "border-sky" : "border-line"}`}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="font-heading font-bold text-ftext">{p.tier}</span>
                    {p.featured && <span className="font-mono-data text-[9px] uppercase tracking-wider text-sky border border-sky/40 px-2 py-0.5">Popular</span>}
                  </div>
                  <div className="flex items-baseline gap-1.5 mb-3">
                    <span className="font-mono-data font-bold text-3xl text-sky">{p.price}</span>
                    <span className="font-mono-data text-xs text-ftext3">{p.unit}</span>
                  </div>
                  <p className="text-xs text-ftext2 mb-2">{p.detail}</p>
                  <p className="text-xs text-ftext3 font-mono-data leading-relaxed">{p.modules}</p>
                </div>
              ))}
            </div>
            <p className="mt-4 text-xs text-ftext3 font-mono-data leading-relaxed">{g.pricing_note}</p>
          </div>
        )}
      </div>

      <div className="border-l-4 border-sky bg-panel px-5 py-4 mb-4">
        <span className="font-heading font-bold text-base md:text-lg text-ftext leading-snug">{g.priority.text}</span>
      </div>

      {/* Channel economics table — hidden until requested */}
      {g.channel_economics && (
        <div className="mt-4">
          <button
            data-testid="channels-toggle-economics"
            type="button"
            onClick={() => setShowEconomics((v) => !v)}
            className="w-full flex items-center justify-between border border-line bg-panel px-5 py-3 hover:border-sky transition-colors"
          >
            <span className="font-mono-data text-xs uppercase tracking-wider text-ftext2">
              {showEconomics ? "Hide channel economics" : "Show channel economics"} · sources · owns · implements · discount
            </span>
            <ChevronRight className={`w-4 h-4 text-sky transition-transform ${showEconomics ? "rotate-90" : ""}`} />
          </button>
          {showEconomics && (
            <div data-testid="channel-economics" className="mt-3 border border-line bg-panel overflow-x-auto">
              <div className="px-4 py-2 border-b border-line bg-ink flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-sky" />
                  <span className="font-mono-data text-[11px] font-black uppercase tracking-[0.18em] text-sky">{g.channel_economics.title}</span>
                </div>
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-line font-mono-data text-[9px] uppercase tracking-wider text-ftext3 bg-ink/40">
                    {g.channel_economics.columns.map((col, i) => (
                      <th key={i} className="text-left px-3 py-1.5">{col}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {g.channel_economics.rows.map((row, i) => (
                    <tr key={i} data-testid={`channel-econ-${i}`} className="border-b border-line/60 last:border-0">
                      <td className="px-3 py-1.5 font-heading font-bold text-ftext align-top">{row.channel}</td>
                      <td className="px-3 py-1.5 text-ftext2 align-top">{row.sources}</td>
                      <td className="px-3 py-1.5 text-ftext2 align-top">{row.owns}</td>
                      <td className="px-3 py-1.5 text-ftext2 align-top">{row.implements}</td>
                      <td className="px-3 py-1.5 text-ftext2 align-top">{row.supports}</td>
                      <td className="px-3 py-1.5 text-sky align-top">{row.discount}</td>
                      <td className="px-3 py-1.5 text-ftext3 italic align-top">{row.activation}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {g.channel_economics.note && (
                <p className="px-4 py-2 text-[11px] text-ftext3 italic leading-snug border-t border-line">{g.channel_economics.note}</p>
              )}
            </div>
          )}
        </div>
      )}
    </SectionShell>
  );
}

export function Ecosystem({ data, common }) {
  const e = data.ecosystem || {};
  const g = data.gtm || {};
  const tierTone = {
    productive: { border: "border-[#10B981]", label: "bg-[#10B981]/10 text-[#10B981] border-[#10B981]/50", dot: "bg-[#10B981]" },
    signed: { border: "border-sky", label: "bg-sky/10 text-sky border-sky/50", dot: "bg-sky" },
    advanced: { border: "border-[#8B5CF6]", label: "bg-[#8B5CF6]/10 text-[#8B5CF6] border-[#8B5CF6]/50", dot: "bg-[#8B5CF6]" },
    initial: { border: "border-line2", label: "bg-panel text-ftext2 border-line2", dot: "bg-ftext3" },
  };
  const tiers = e.tiers || [];
  return (
    <SectionShell id="ecosystem" eyebrow={e.eyebrow} headline={e.headline} {...common}>
      {e.subhead && (
        <p className="text-base md:text-lg text-ftext2 max-w-3xl leading-relaxed mb-5">{e.subhead}</p>
      )}

      {/* Mechanism band */}
      {e.mechanism && (
        <div data-testid="ecosystem-mechanism" className="grid gap-2 md:grid-cols-3 mb-5">
          {e.mechanism.map((m) => (
            <div key={m.key} data-testid={`ecosystem-mechanism-${m.key}`} className="border border-line bg-panel p-3 flex items-start gap-2">
              <div className="flex h-8 w-8 items-center justify-center border border-sky/40 bg-sky/10 shrink-0">
                <Icon name={m.icon} className="w-4 h-4 text-sky" />
              </div>
              <div>
                <div className="font-heading font-black text-sm text-ftext leading-tight">{m.title}</div>
                <p className="text-[11px] text-ftext2 leading-snug mt-0.5">{m.body}</p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* 4-tier status legend */}
      <div className="flex flex-wrap items-center gap-3 mb-3 font-mono-data text-[11px] font-bold uppercase tracking-[0.14em]">
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-[#10B981]" /> Signed & revenue-producing</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-sky" /> Signed · activation</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-[#8B5CF6]" /> Advanced</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-ftext3" /> Initial</span>
      </div>

      <div className="space-y-2">
        {tiers.map((tier) => {
          const tone = tierTone[tier.tone] || tierTone.initial;
          return (
            <div key={tier.key} data-testid={`ecosystem-tier-${tier.tone}`} className={`border-l-4 ${tone.border} bg-panel px-3 md:px-4 py-3`}>
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <span className={`font-mono-data text-[10px] font-black uppercase tracking-[0.18em] px-2 py-0.5 border ${tone.label}`}>{tier.label}</span>
                <span className="font-mono-data text-[10px] font-bold uppercase tracking-wider text-ftext3">{tier.partners.length} partner{tier.partners.length === 1 ? "" : "s"}</span>
                {tier.detail && <span className="font-mono-data text-[10px] text-ftext3 italic">{tier.detail}</span>}
              </div>
              <div className="flex flex-wrap gap-2">
                {tier.partners.map((p) => (
                  <PartnerLogoBig key={p.name} partner={p} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </SectionShell>
  );
}

/* ------------------------------------------------------------ Pilot → Rollout (new) */
export function PilotToRollout({ data, common }) {
  const s = data.pilot_to_rollout;
  if (!s) return null;
  const t = s.table;
  return (
    <SectionShell id="pilot_to_rollout" eyebrow={s.eyebrow} headline={s.headline} {...common}>
      {s.subhead && <p className="text-base md:text-lg text-ftext2 max-w-4xl leading-relaxed mb-6">{s.subhead}</p>}

      {/* Top half — 3-step horizontal flow */}
      <div data-testid="pilot-to-rollout-flow" className="grid gap-4 md:grid-cols-3 mb-5">
        {s.flow.map((step, i) => (
          <div key={i} className="relative border border-line bg-panel p-5 flex flex-col">
            <div className="flex items-start gap-3 mb-3">
              <div className="flex h-11 w-11 items-center justify-center border border-sky/40 bg-sky/10 shrink-0">
                <Icon name={step.icon} className="w-5 h-5 text-sky" />
              </div>
              <div>
                <div className="font-mono-data text-[10px] font-black uppercase tracking-[0.18em] text-sky">Step {i + 1}</div>
                <h3 className="font-heading font-black text-lg text-ftext leading-tight mt-0.5">{step.title}</h3>
              </div>
            </div>
            <p className="text-sm text-ftext2 leading-snug">{step.body}</p>
          </div>
        ))}
      </div>
      {s.conversion_drivers && (
        <p className="text-xs md:text-sm text-ftext3 italic max-w-4xl leading-snug mb-6">{s.conversion_drivers}</p>
      )}

      {/* Bottom half — table */}
      <div data-testid="pilot-to-rollout-table" className="border border-line bg-panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-ink">
                {t.columns.map((c) => (
                  <th key={c} className="text-left px-3 py-2.5 font-mono-data text-[10px] font-black uppercase tracking-[0.18em] text-ftext3 border-b border-line">{c}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {t.rows.map((r, i) => (
                <tr key={i} className="border-b border-line last:border-b-0 hover:bg-ink/40 transition-colors">
                  <td className="px-3 py-2.5 font-heading font-black text-ftext">{r.customer}</td>
                  <td className="px-3 py-2.5 text-ftext2">{r.use_case}</td>
                  <td className="px-3 py-2.5 text-ftext2">{r.status}</td>
                  <td className="px-3 py-2.5 text-ftext2">{r.decision}</td>
                  <td className="px-3 py-2.5 text-ftext text-right font-mono-data">{r.plants}</td>
                  <td className="px-3 py-2.5 text-ftext text-right font-mono-data">{r.arr_per_plant}</td>
                  <td className="px-3 py-2.5 text-[#10B981] text-right font-mono-data font-bold">{r.potential}</td>
                </tr>
              ))}
              {t.total && (
                <tr className="bg-ink/60">
                  <td className="px-3 py-2.5 font-heading font-black text-ftext uppercase tracking-wider text-xs">Total</td>
                  <td colSpan={3}></td>
                  <td className="px-3 py-2.5 text-ftext text-right font-mono-data font-bold">{t.total.plants}</td>
                  <td className="px-3 py-2.5 text-ftext text-right font-mono-data">{t.total.arr_per_plant}</td>
                  <td className="px-3 py-2.5 text-[#10B981] text-right font-mono-data font-black">{t.total.potential}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {s.footer && (
        <div data-testid="pilot-to-rollout-footer" className="mt-5 border-l-4 border-[#10B981] bg-panel/60 px-4 py-3 max-w-4xl">
          <p className="font-heading font-bold text-base md:text-lg text-ftext leading-snug">{s.footer}</p>
        </div>
      )}
    </SectionShell>
  );
}

/* ------------------------------------------------------------ Investor Return (new) */
export function InvestorReturn({ data, common }) {
  const s = data.investor_return;
  if (!s) return null;
  const t = s.table;
  return (
    <SectionShell id="investor_return" eyebrow={s.eyebrow} headline={s.headline} {...common}>
      {s.subhead && <p className="text-base md:text-lg text-ftext2 max-w-4xl leading-relaxed mb-6">{s.subhead}</p>}

      <div className="grid gap-6 lg:grid-cols-5">
        {/* Left column: who buys and why */}
        <div data-testid="investor-return-left" className="lg:col-span-2 space-y-4">
          <div className="border border-line bg-panel p-5">
            <div className="font-mono-data text-[10px] font-black uppercase tracking-[0.18em] text-sky mb-2">{s.left.who_title}</div>
            <p className="text-sm md:text-base text-ftext leading-snug">{s.left.who_body}</p>
          </div>
          <div className="border border-line bg-panel p-5">
            <div className="font-mono-data text-[10px] font-black uppercase tracking-[0.18em] text-[#10B981] mb-2">{s.left.why_title}</div>
            <p className="text-sm md:text-base text-ftext leading-snug">{s.left.why_body}</p>
          </div>
        </div>

        {/* Right column: scenario table */}
        <div data-testid="investor-return-right" className="lg:col-span-3 border border-line bg-panel overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-ink">
                  {t.columns.map((c) => (
                    <th key={c} className="text-left px-3 py-2.5 font-mono-data text-[10px] font-black uppercase tracking-[0.18em] text-ftext3 border-b border-line">{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {t.rows.map((r, i) => (
                  <tr key={i} className="border-b border-line last:border-b-0">
                    <td className="px-3 py-3 font-heading font-black text-ftext">{r.scenario}</td>
                    <td className="px-3 py-3 text-ftext2 font-mono-data">{r.year}</td>
                    <td className="px-3 py-3 text-ftext font-mono-data">{r.arr}</td>
                    <td className="px-3 py-3 text-ftext2 font-mono-data">{r.mult}</td>
                    <td className="px-3 py-3 text-ftext font-mono-data">{r.value}</td>
                    <td className="px-3 py-3 text-ftext2 font-mono-data">{r.stake}</td>
                    <td className="px-3 py-3 text-[#10B981] font-mono-data font-bold">{r.multiple}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {t.footnote && <div className="px-4 py-2.5 text-[11px] italic text-ftext3 border-t border-line">{t.footnote}</div>}
        </div>
      </div>
    </SectionShell>
  );
}


export const SECTION_COMPONENTS = {
  welcome: Welcome, problem: Problem, solution: Solution,
  architecture: Architecture,
  end_user_value: EndUserValue,
  trust: TrustProvenance,
  capabilities: Capabilities, maturity: Maturity, traction: Traction, pilots: PaidPilots,
  pilot_to_rollout: PilotToRollout,
  moat: Moat,
  why_we_win: WhyWeWin,
  competition: Competition,
  competition_dimensions: CompetitionDimensions,
  verticals: Verticals, market: Market,
  gtm: GTM, channels: Channels, ecosystem: Ecosystem,
  financials: Financials, channelmix: ChannelMix, raise: Raise, scenario: FutureScenario,
  investor_return: InvestorReturn,
  leadership: Leadership, founders: Founders, sandbox: Sandbox, business_model: BusinessModel, cta: FinalCTA,
};
